import { createElement, generateElementId, useTextFieldIdCounter } from '@/helper/element-factory'
import {
  APP_STORE_META,
  AppStore,
  DOCUMENT_LINKED_VARIABLE,
  DOCUMENT_SENDER_ID,
  DocumentStatus,
  ELEMENTS_LOOKUP,
  ELEMENTS_META,
  ElementTypes,
  GROUP_LOOKUP,
  GroupFields,
  GroupFieldsOptions,
  ICustomFieldsLink,
  ICustomFieldsLinkItem,
  IElement,
  IPage,
  IRecipient,
  IResponsiveStylesOptions,
  IUpdateElementOption,
  IUpdateGroupOption,
  IUpdateSectionElementOption,
  LineItem,
  MAX_HEADER_FOOTER_COMBINED_HEIGHT,
  ObjectForLinkedField,
  PAGE_SECTIONS,
  PriceTypeEnum,
  ProductListElementOptions,
  RecipientEntityTypeEnum,
  UpdateSignatureParams,
  assignableFields,
  applyBottomPreferPositionSync,
  buildHeaderFooterCloneIdMapFromPages,
  buildHeaderFooterElementIdMapForTargetPage,
  checkForDuplicatesFieldKeyInCustomValueLinkage,
  bottomPreferLayoutNeedsWrite,
  collectBottomPreferDraggableIdsFromPage,
  computeBottomPreferGeometryFromStoredLayout,
  cloneCheckboxGroupForReplicatedSectionPage,
  collectElementIdsInSectionSubtree,
  filterOutCheckboxGroupReplicaFootprint,
  findCheckboxGroupsWhollyOnHostInSection,
  findHeaderFooterReplicaCheckboxGroupId,
  getHeaderOrFooterHostForWhollyContainedCheckboxGroup,
  hasCheckboxGroupReplicaFootprintOnPage,
  cloneGroupWithNewIds,
  cloneSectionWithStableFillableIds,
  cloneWithNewIds,
  fillableFields,
  findElementOnSinglePage,
  flatChildrens,
  floatingElementsSet,
  treeLevelContainsElementId,
  getAdditionalRecipientsWithRoles,
  getFloatingBoundSectionForElementOnPage,
  getRecipientParamsFromContact,
  remapKeysForCVLinkage,
  removeFillableElementsWhichIsPartOfGroup,
  removeUserFromRecipientIfElementIsNotAssigned,
  updateRecipientRolesIfElementIsAssigned,
} from '@gohighlevel/ghl-proposals-common'
import { cloneDeep, debounce } from 'lodash'
import { nextTick } from 'vue'

import { useElementOption } from '@/composable/elementOptions'
import { Editor } from '@tiptap/vue-3'
import { defineStore } from 'pinia'
import { useAppStore } from './app'

const DEBOUNCE_DELAY = 300
const SECTION_PROPAGATION_DEBOUNCE_DELAY = 3000 // 3 seconds for section element propagation

const debouncedCreateRedoStack = debounce(function (this: any) {
  this.createRedoStack()
}, DEBOUNCE_DELAY)

// Store pending propagation calls and their debounced functions (clone full section so clone ids are preserved)
const pendingSectionPropagations: Map<
  string,
  {
    elementId: string
    key: string
    value: any
    sectionType: string
    hostPageId: string
    debouncedFn: ReturnType<typeof debounce>
  }
> = new Map()

const createDebouncedPropagateSectionOption = (hostPageId: string, sectionType: string, propagationKey: string) => {
  return debounce(function (this: any) {
    this.updateSectionChangeToEveryPage(hostPageId, sectionType)
    this.updateEditors = !this.updateEditors
    pendingSectionPropagations.delete(propagationKey)
    scheduleAllPagesFloatingLayoutSync(this)
  }, SECTION_PROPAGATION_DEBOUNCE_DELAY)
}

const floatingLayoutByPageDebouncers = new Map<string, ReturnType<typeof debounce>>()
let floatingLayoutAllPagesDebouncer: ReturnType<typeof debounce> | null = null

function schedulePageFloatingLayoutSync(store: any, pageId: string) {
  let d = floatingLayoutByPageDebouncers.get(pageId)
  if (!d) {
    d = debounce(() => {
      store.recomputeFloatingLayoutPositionsForPage(pageId)
    }, 48)
    floatingLayoutByPageDebouncers.set(pageId, d)
  }
  d()
}

function scheduleAllPagesFloatingLayoutSync(store: any) {
  if (!floatingLayoutAllPagesDebouncer) {
    floatingLayoutAllPagesDebouncer = debounce(() => {
      store.recomputeFloatingLayoutPositionsForAllPages()
    }, 48)
  }
  floatingLayoutAllPagesDebouncer()
}

function shouldScheduleFloatingLayoutAfterOption(key: string): boolean {
  return key !== 'isGhost'
}

function shouldScheduleFloatingLayoutAfterStyle(key: string, elementId: string, pages: IPage[]): boolean {
  if (key === 'position') {
    const el = flatChildrens(pages).find(e => e.id === elementId)
    if (el?.component?.isDraggable) return false
  }
  return true
}

/**
 * Section sync does not target a single block. The whole header (or footer) is the unit:
 * we replace every other page's section with a clone of the host page's section. So all
 * blocks (text, images, etc.) stay in the same order; the "same" image is the one at the
 * same path in the tree. We use that path only to copy height/width when the clone has
 * none (from host, then default 0 as fallback before the image loads).
 */
function syncSectionMediaDimensions(cloneNode: IElement, hostNode: IElement | undefined): void {
  if ((cloneNode.type === ELEMENTS_LOOKUP.IMAGE || cloneNode.type === ELEMENTS_LOOKUP.VIDEO) && cloneNode.responsiveStyles) {
    const hasSrc =
      (cloneNode.type === ELEMENTS_LOOKUP.IMAGE && (cloneNode.component?.options as any)?.src) ||
      (cloneNode.type === ELEMENTS_LOOKUP.VIDEO && (cloneNode.component?.options as any)?.html)
    if (hasSrc) {
      const large = cloneNode.responsiveStyles.large as Record<string, unknown>
      const hostLarge = hostNode?.responsiveStyles?.large as Record<string, unknown> | undefined
      if (large.height == null || large.height === '') {
        large.height = hostLarge?.height != null && hostLarge.height !== '' ? hostLarge.height : 0
      }
      if (large.width == null || large.width === '') {
        large.width = hostLarge?.width != null && hostLarge.width !== '' ? hostLarge.width : 0
      }
    }
  }
  const cloneCh = cloneNode.children
  const hostCh = hostNode?.children
  if (Array.isArray(cloneCh) && Array.isArray(hostCh)) {
    for (let i = 0; i < cloneCh.length && i < hostCh.length; i++) {
      const c = cloneCh[i]
      const h = hostCh[i]
      if (c) syncSectionMediaDimensions(c, h)
    }
  }
}

let shouldCreateNewEditorGroup = true

const { getNextCount } = useTextFieldIdCounter()

export const useBuilderStore = defineStore('builderStore', {
  state: (): AppStore => ({
    activeElementId: '',
    groups: [] as GroupFields<GroupFieldsOptions>[],
    activeGroup: {} as GroupFields<GroupFieldsOptions>,
    editor: undefined,
    pages: [],
    hover: false,
    updateEditors: false,
    focusedElement: {} as IElement,
    headerSelected: false,
    footerSelected: false,
    showHeaderSettings: false,
    isHeaderSectionActive: false,
    isFooterSectionActive: false,
    enablePageNumbering: true,
    pageNumberLocation: 'footer',
    pageNumberFormat: 'number-only',
    undoRedoStack: [],
    undoRedoStackPointer: 0,
    undoSelected: false,
    moveElementDragState: false,
    status: DocumentStatus.DRAFT,
    flatCustomValues: {},
    customValueLinkage: {} as Record<string, ICustomFieldsLink>,
    loadedFonts: [] as Array<string>, // Fonts that have been loaded from the FontFamilyDropdown (list) component - This is for not to load the same font again in the editor
    fontsToLoad: [] as Array<string>, // Fonts that is being used in the editor / entire document - This font will get saved in document for loading in document viewer side
    headerSection: undefined,
    footerSection: undefined,
    headerFooterCloneIdMap: {},
    sectionHeightValidationError: null as string | null,
  }),
  getters: {
    isDocumentEditable: state => {
      return state.status === DocumentStatus.DRAFT
    },
    activeElement: state => {
      const page = state.pages.find(page => {
        return page.id === state.activeElementId
      })
      if (page) return page

      // Check header/footer sections directly first
      for (const pageItem of state.pages) {
        const headerSection = pageItem?.component?.options?.[PAGE_SECTIONS.HEADERS]
        if (headerSection && headerSection.id === state.activeElementId) {
          return headerSection
        }
        const footerSection = pageItem?.component?.options?.[PAGE_SECTIONS.FOOTERS]
        if (footerSection && footerSection.id === state.activeElementId) {
          return footerSection
        }
      }

      // Then check all other elements via flatChildrens
      const activeElement = flatChildrens(state.pages).find((element: IElement) => element?.id === state.activeElementId)
      return activeElement
    },
    hasLineItems: state => {
      return flatChildrens(state.pages).find((element: IElement) => element.type === ELEMENTS_LOOKUP.PRODUCT_LIST)?.id ? true : false
    },
    totalLineItemsLength: state => {
      return flatChildrens(state.pages)
        .filter(
          child => child.type === ELEMENTS_LOOKUP.PRODUCT_LIST && (child as IElement<ProductListElementOptions>).component.options.lineItems
        )
        .map(child => (child as IElement<ProductListElementOptions>).component.options.lineItems.length)
        .reduce((acc, count) => acc + count, 0)
    },
    getAllLineItems: state => {
      return flatChildrens(state.pages)
        .filter(
          child => child.type === ELEMENTS_LOOKUP.PRODUCT_LIST && (child as IElement<ProductListElementOptions>).component.options.lineItems
        )
        .reduce((acc, elem) => {
          acc.push(...elem.component.options.lineItems)
          return acc
        }, [] as LineItem[])
    },
  },
  actions: {
    setStatus(status: DocumentStatus) {
      this.status = status
    },
    findElementInChildren(
      id: string,
      children: any[],
      parent: any,
      parentType: ElementTypes
    ): {
      element: IElement
      parent: IElement
      parentType: ElementTypes
    } | null {
      for (const child of children) {
        if (child && child.id === id) {
          return {
            element: child,
            parent,
            parentType,
          }
        }

        if (child?.children && child?.children?.length > 0) {
          const found = this.findElementInChildren(id, child.children, child, child.type)
          if (found) return found
        }
      }
      return null
    },
    getElementById(id: string) {
      for (const page of this.pages) {
        if (page.id === id) {
          return {
            element: page,
            parent: null,
            parentType: null,
          }
        }

        const found = this.findElementInChildren(id, page.children, page, ELEMENTS_LOOKUP.PAGE)

        if (found) return found

        const headers = page.component?.options?.headers
        const foundInHeaders = this.findElementInChildren(id, headers?.children ?? [], headers, ELEMENTS_LOOKUP.PAGE_SECTION)
        if (foundInHeaders) return foundInHeaders

        const footers = page.component?.options?.footers
        const foundInFooters = this.findElementInChildren(id, footers?.children ?? [], footers, ELEMENTS_LOOKUP.PAGE_SECTION)
        if (foundInFooters) return foundInFooters
      }

      return null
    },
    getPageIdByElementId(elementId: string) {
      return this.pages.find(
        page =>
          treeLevelContainsElementId(page.children, elementId) ||
          treeLevelContainsElementId(page.component?.options?.headers?.children, elementId) ||
          treeLevelContainsElementId(page.component?.options?.footers?.children, elementId)
      )?.id
    },
    getPageIdBySectionElementId(elementId: string, sectionType: string) {
      return this.pages.find(page => treeLevelContainsElementId(page.component?.options[sectionType]?.children, elementId))?.id
    },
    createRedoStack(skipUnsavedChanges?: boolean) {
      if (!this.isDocumentEditable) return
      this.undoRedoStack.push(cloneDeep(this.pages))
      this.undoRedoStackPointer = this.undoRedoStack.length - 1
      const appStore = useAppStore()
      if (!skipUnsavedChanges) {
        appStore.setUnsavedChanges(true)
        this.undoSelected = false
      }
    },
    undo() {
      if (!this.isDocumentEditable) return
      if (this.undoRedoStackPointer > 0) {
        this.undoRedoStackPointer--
        const page = cloneDeep(this.undoRedoStack[this.undoRedoStackPointer])
        this.pages = page
        this.updateEditors = !this.updateEditors
        this.undoSelected = true
      }
    },
    redo() {
      if (!this.isDocumentEditable) return
      if (this.undoRedoStackPointer < this.undoRedoStack.length - 1) {
        this.undoRedoStackPointer++
        const page = cloneDeep(this.undoRedoStack[this.undoRedoStackPointer])
        this.pages = page
        this.updateEditors = !this.updateEditors
      }
    },
    setPages(pages: IPage[]) {
      this.pages = pages
      this.headerFooterCloneIdMap = buildHeaderFooterCloneIdMapFromPages(pages)

      // Resolve header/footer source: use first page if it has section, else first page that has it (e.g. PDF added at front)
      const firstPage = pages[0]
      if (firstPage) {
        const headerSection =
          firstPage.component?.options?.[PAGE_SECTIONS.HEADERS] ??
          pages.find(p => p.component?.options?.[PAGE_SECTIONS.HEADERS])?.component?.options?.[PAGE_SECTIONS.HEADERS]
        const footerSection =
          firstPage.component?.options?.[PAGE_SECTIONS.FOOTERS] ??
          pages.find(p => p.component?.options?.[PAGE_SECTIONS.FOOTERS])?.component?.options?.[PAGE_SECTIONS.FOOTERS]

        if (headerSection) {
          this.headerSection = headerSection
          this.headerSelected = true
        } else {
          this.headerSection = undefined
        }

        if (footerSection) {
          this.footerSection = footerSection
          this.footerSelected = true
        } else {
          this.footerSection = undefined
        }

        // Initialize page numbering options from first page, or set defaults
        const enablePageNumbering = firstPage.component?.options?.enablePageNumbering
        const pageNumberLocation = firstPage.component?.options?.pageNumberLocation
        const pageNumberFormat = firstPage.component?.options?.pageNumberFormat

        // Set defaults if not present
        if (enablePageNumbering === undefined) {
          this.enablePageNumbering = true
          // Apply to all pages
          pages.forEach(page => {
            this.updateElementOption({
              id: page.id,
              key: 'enablePageNumbering',
              value: true,
              type: ELEMENTS_LOOKUP.PAGE,
            })
          })
        } else {
          this.enablePageNumbering = enablePageNumbering
        }

        if (pageNumberLocation === undefined || pageNumberLocation === '') {
          this.pageNumberLocation = 'footer'
          // Apply to all pages
          pages.forEach(page => {
            this.updateElementOption({
              id: page.id,
              key: 'pageNumberLocation',
              value: 'footer',
              type: ELEMENTS_LOOKUP.PAGE,
            })
          })
        } else {
          this.pageNumberLocation = pageNumberLocation
        }

        if (pageNumberFormat === undefined || pageNumberFormat === '') {
          this.pageNumberFormat = 'number-only'
          // Apply to all pages
          pages.forEach(page => {
            this.updateElementOption({
              id: page.id,
              key: 'pageNumberFormat',
              value: 'number-only',
              type: ELEMENTS_LOOKUP.PAGE,
            })
          })
        } else {
          this.pageNumberFormat = pageNumberFormat
        }
      } else {
        this.headerSection = undefined
        this.footerSection = undefined
      }

      if (this.isDocumentEditable && (this.headerSection || this.footerSection)) {
        this.syncHeaderFooterToAllPages()
      }
      this.createRedoStack(true)
    },
    setActiveGroup(groupId: string | null) {
      if (!this.isDocumentEditable) return
      if (!groupId) {
        this.activeGroup = null
      }
      this.activeGroup = this.groups.find(g => g.id === groupId)
      this.createRedoStack(true)
    },
    setActiveGroupByElementId(elementId: string) {
      if (!this.isDocumentEditable) return
      const group = this.groups.find(g => g.children.some(c => c.elementId === elementId))
      if (group) {
        this.activeGroup = group
      }
    },
    setGroups(groups: GroupFields<GroupFieldsOptions>[]) {
      this.groups = groups
      this.syncGroup()
      if (this.isDocumentEditable) {
        this.replicateAllHeaderFooterCheckboxGroupsAcrossPages()
      }
      if (!this.isDocumentEditable) return
      this.createRedoStack(true)
    },
    updateGroup(group: GroupFields<GroupFieldsOptions>) {
      if (!this.isDocumentEditable) return
      this.groups = [...this.groups, group]
      this.replicateAllHeaderFooterCheckboxGroupsAcrossPages()
      this.createRedoStack(true)
    },
    deleteGroupAndGroupElements(id: string) {
      if (!this.isDocumentEditable) return
      const group = this.groups.find(g => g.id === id)
      group?.children.forEach(({ elementId }) => this.deleteElement(elementId))
      this.groups = this.groups.filter(g => g.id !== id)
      this.createRedoStack(true)
    },
    deleteGroup(id: string) {
      if (!this.isDocumentEditable) return
      this.groups = this.groups.filter(g => g.id !== id)
      this.createRedoStack(true)
    },
    updateCustomValueLinkage(elementId: string, type: string, customLinkItem: ICustomFieldsLinkItem) {
      if (!this.isDocumentEditable) return
      if (this.customValueLinkage[elementId]) {
        // check the type and push the customLinkItem to the existing customFields array
        const cf = this.customValueLinkage[elementId].customFields
        if (cf && cf.length > 0) {
          const isFieldKeyExist = cf.findIndex(c => c.fieldKey === customLinkItem.fieldKey)
          if (isFieldKeyExist === -1) {
            this.customValueLinkage[elementId].customFields.push(customLinkItem)
          }
        }
      } else {
        const customFields = []
        customFields.push(customLinkItem)
        this.customValueLinkage[elementId] = {
          type,
          customFields,
        }
      }
      this.syncContactIdInLinkedCustomValues()
      this.createRedoStack(true)
    },
    updateCustomValueLinkageItem(elementId: string, addCustomLinkItem: ICustomFieldsLinkItem, removeCustomLinkItem: ICustomFieldsLinkItem) {
      if (this.customValueLinkage[elementId]) {
        const customFields = this.customValueLinkage[elementId].customFields
        const index = customFields.findIndex(item => item.fieldKey === removeCustomLinkItem?.fieldKey)
        if (index !== -1) {
          customFields.splice(index, 1, addCustomLinkItem)
        }
      }
    },
    addNewElementsToCustomValueLinkage(customValueLink: Record<string, ICustomFieldsLink>) {
      if (!this.isDocumentEditable) return
      for (const key in customValueLink) {
        if (!(key in this.customValueLinkage)) {
          this.customValueLinkage[key] = customValueLink[key]
        }
      }
      this.syncContactIdInLinkedCustomValues()
    },
    setCustomValueLinkage(customValueLinkage: Record<string, ICustomFieldsLink>) {
      this.customValueLinkage = customValueLinkage
      if (!this.isDocumentEditable) return
      this.syncContactIdInLinkedCustomValues()
      this.createRedoStack(true)
    },
    deleteCustomValueLinkage(elementId: string, fieldKey: string) {
      if (elementId && this.customValueLinkage[elementId] && fieldKey) {
        const customFields = this.customValueLinkage[elementId].customFields
        if (customFields && customFields.length > 0) {
          const index = customFields.findIndex(c => c.fieldKey === fieldKey)
          if (index > -1) {
            customFields.splice(index, 1)
          }
        }
        if (this.customValueLinkage[elementId].customFields.length === 0) {
          delete this.customValueLinkage[elementId]
        }
        this.createRedoStack(true)
      }
    },
    getCustomValueLinkage(elementId: string) {
      return this.customValueLinkage[elementId]?.customFields || []
    },
    getAllLinkedDocumentVariables() {
      return Object.values(this.customValueLinkage)
        .map(linkage => linkage.customFields)
        .flat()
        .filter(field => field && field?.objectType === ObjectForLinkedField.DOCUMENT_VARIABLE)
        .map(field => {
          return {
            fieldKey: field.fieldKey,
            value: field.value,
            type: DOCUMENT_LINKED_VARIABLE,
          }
        })
    },

    addElementToExistingGroup(elementInfo: { pageId: string; elementId: string }, groupId: string) {
      this.groups?.forEach(g => {
        if (g.id === groupId) {
          g.children.push(elementInfo)
        }
      })
    },
    addElementsToExistingGroup(elementInfo: { pageId: string; elementId: string }[], groupId: string) {
      this.groups?.forEach(g => {
        if (g.id === groupId) {
          const cgMap = g.children.map(({ elementId }) => elementId)
          elementInfo.forEach(({ elementId, pageId }) => {
            if (!cgMap.includes(elementId)) {
              g.children.push({ elementId, pageId })
            }
          })
        }
      })
    },
    getElementGroup(elementId: string) {
      return this.groups.find(g => {
        const cgChild = g.children.map((child: any) => child.elementId)
        if (cgChild.includes(elementId)) {
          return g
        }
      })
    },
    updateOneGroup(groupId: string, children: { pageId: string; elementId: string }[], groupType?: string) {
      if (groupId) {
        this.groups?.forEach(g => {
          if (g.id === groupId) {
            g.children = children
            if (groupType) {
              g.component.options.groupType = groupType
            }
          }
        })
      }
      if (this.isDocumentEditable) {
        this.replicateAllHeaderFooterCheckboxGroupsAcrossPages()
      }
    },
    updateGroupOption(params: IUpdateGroupOption) {
      if (!this.isDocumentEditable) return
      const { id, value, key } = params
      const groupIndex = this.groups.findIndex(g => g.id === id)
      if (groupIndex > -1) {
        const g = this.groups[groupIndex]
        const opts = g.component.options as Record<string, GroupFieldsOptions[keyof GroupFieldsOptions] | number>
        opts[key] = value
        if (key !== ELEMENTS_META.FIELD_ID) {
          const host = getHeaderOrFooterHostForWhollyContainedCheckboxGroup(g, this.pages)
          if (host) {
            const cloneMap = this.headerFooterCloneIdMap ?? {}
            for (const page of this.pages) {
              if (page.id === host.pageId) continue
              const replicaId = findHeaderFooterReplicaCheckboxGroupId(this.groups, g, host.pageId, page.id, cloneMap, this.pages)
              if (!replicaId) continue
              const replica = this.groups.find(x => x.id === replicaId)
              if (replica?.component?.options) {
                const ro = replica.component.options as Record<string, GroupFieldsOptions[keyof GroupFieldsOptions] | number>
                ro[key] = value
              }
            }
          }
        }
      }
      this.createRedoStack()
      return
    },
    updateFocusedElement(element?: IElement) {
      if (!this.isDocumentEditable) return
      this.focusedElement = element
    },
    changeHoverState(state: boolean) {
      if (!this.isDocumentEditable) return
      this.hover = state
    },
    deletePage(pageId: string) {
      if (!this.isDocumentEditable) return
      const pageIndex = this.pages.findIndex(page => page.id === pageId)
      if (pageIndex > -1) {
        this.pages.splice(pageIndex, 1)

        if (pageIndex === 0 && this.pages.length > 0) {
          const newFirstPage = this.pages[0]
          this.headerSection = newFirstPage?.component?.options?.[PAGE_SECTIONS.HEADERS]
          this.footerSection = newFirstPage?.component?.options?.[PAGE_SECTIONS.FOOTERS]
          const activeId = this.activeElementId
          if (activeId) {
            const stillInTree =
              this.pages.some(
                p =>
                  p.component?.options?.[PAGE_SECTIONS.HEADERS]?.id === activeId ||
                  p.component?.options?.[PAGE_SECTIONS.FOOTERS]?.id === activeId
              ) || flatChildrens(this.pages).some((el: IElement) => el?.id === activeId)
            if (!stillInTree) {
              this.updateActiveElement(null)
            }
          }
        }
      }
      this.syncGroup()
      this.syncCustomFieldsForLinkage()
      this.createRedoStack()
    },
    duplicatePage(pageId: string) {
      if (!this.isDocumentEditable) return

      const pageIndex = this.pages.findIndex(page => page.id === pageId)
      if (pageIndex === -1) return

      const page = this.pages[pageIndex]
      const { children, ...newPageKeys } = cloneDeep(page)

      const generateNewChildren = (children: IElement[]): IElement[] =>
        children?.map(child => ({
          ...child,
          id: generateElementId(),
          children: generateNewChildren(child.children),
        }))

      const newChildren = generateNewChildren(children)

      const newPage = {
        ...newPageKeys,
        children: newChildren,
        id: generateElementId(),
      }

      this.pages.splice(pageIndex + 1, 0, newPage)
      this.createRedoStack()
    },
    deleteElement(elementId: string) {
      if (!this.isDocumentEditable) return

      // Function to recursively search and delete an element inside nested children
      const deleteNestedElement = (children: any[]) => {
        const elementIndex = children.findIndex(child => child?.id === elementId)
        if (elementIndex !== -1) {
          children.splice(elementIndex, 1)
          return true // Element found and deleted
        }

        // Check if any child contains nested children and search recursively
        for (const child of children) {
          if (child?.children?.length) {
            const isDeleted = deleteNestedElement(child.children)
            if (isDeleted) {
              // Only remove empty containers (COLUMN / ROW). Block elements
              // like TEXT / IMAGE / TABLE / VIDEO / PRODUCT_LIST can legitimately
              // exist with zero fillable children, so they must not be
              // cascade-deleted just because their last fillable child was.
              const isContainer = child.type === ELEMENTS_LOOKUP.COLUMN || child.type === ELEMENTS_LOOKUP.ROW
              if (isContainer && child.children.length === 0) {
                const childIndex = children.indexOf(child)
                children.splice(childIndex, 1)
              }
              return true
            }
          }
        }

        return false
      }

      // Iterate over pages to find and delete the element
      const pageIndex = this.pages.findIndex(page =>
        page.children?.some(element => element.id === elementId || deleteNestedElement(element.children || []))
      )

      if (pageIndex > -1) {
        const page = this.pages[pageIndex]

        // Try to delete the element at the top level first
        const elementIndex = page.children?.findIndex(element => element?.id === elementId)

        if (elementIndex !== undefined && elementIndex > -1) {
          const deletedElement = page.children[elementIndex]
          page.children.splice(elementIndex, 1)

          // Check if the deleted element was a COLUMN
          if (deletedElement.type === ELEMENTS_LOOKUP.COLUMN) {
            // If it was a column, check if the parent (row) should be deleted
            const parentRow = page.children.find(child => child.children?.includes(deletedElement))
            if (parentRow && parentRow.children.length === 0) {
              const rowIndex = page.children.indexOf(parentRow)
              page.children.splice(rowIndex, 1)
            }
          }
        } else {
          // If not found at the top level, try to delete in nested elements
          page.children.forEach(child => {
            if (child.children?.length) {
              deleteNestedElement(child.children)
            }
          })
        }

        // Clean up empty columns and rows, and replace row -> column -> element with the element directly
        page.children = page.children.flatMap(child => {
          // If child is a COLUMN and has no children, remove it
          if (child.type === ELEMENTS_LOOKUP.COLUMN && child.children.length === 0) {
            return []
          }

          // If child is a ROW with only one column, and that column has only one child
          if (
            child.type === ELEMENTS_LOOKUP.ROW &&
            child.children.length === 1 &&
            child.children[0].type === ELEMENTS_LOOKUP.COLUMN &&
            child.children[0].children.length === 1
          ) {
            // Replace the row with the element inside the column
            return [child.children[0].children[0]]
          }

          // If child is a ROW and has no children, remove it
          if (child.type === ELEMENTS_LOOKUP.ROW && child.children.length === 0) {
            return []
          }

          return child // Keep this child
        })
      }

      this.createRedoStack()
    },
    deleteElementInSection(elementId: string, sectionType = PAGE_SECTIONS.HEADERS) {
      if (!this.isDocumentEditable) return

      const cloneMap = this.headerFooterCloneIdMap ?? {}

      // Collect element id + all descendants. For non-fillable blocks only: also remove consecutive
      // trailing fillable siblings (they follow the block in column.children). Skip that cascade when
      // deleting a fillable/draggable itself — header/footer often store many independent floats as siblings.
      const addDescendantIds = (el: any, ids: Set<string>) => {
        if (el?.id) ids.add(el.id)
        if (Array.isArray(el?.children)) el.children.forEach((c: any) => addDescendantIds(c, ids))
      }
      const findAndCollectIds = (children: any[], targetId: string): Set<string> | null => {
        if (!children?.length) return null
        for (let i = 0; i < children.length; i++) {
          const child = children[i]
          if (child?.id === targetId) {
            const ids = new Set<string>()
            addDescendantIds(child, ids)
            const deletingFloatingOrDraggable = Boolean(child?.component?.isDraggable) || floatingElementsSet.has(child?.type)
            if (!deletingFloatingOrDraggable) {
              for (let j = i + 1; j < children.length; j++) {
                const sib = children[j]
                if (sib?.id && (sib.component?.isDraggable || floatingElementsSet.has(sib.type))) {
                  ids.add(sib.id)
                  addDescendantIds(sib, ids)
                } else break
              }
            }
            return ids
          }
          const found = findAndCollectIds(child?.children ?? [], targetId)
          if (found) return found
        }
        return null
      }

      let subtreeIds = new Set<string>([elementId])
      for (const page of this.pages) {
        const pageSection = page.component?.options?.[sectionType]
        if (!pageSection?.children?.length) continue
        const found = findAndCollectIds(pageSection.children, elementId)
        if (found) {
          subtreeIds = found
          break
        }
      }

      const idsToDelete = new Set<string>()
      subtreeIds.forEach(id => {
        idsToDelete.add(id)
        const clones = cloneMap[id]
        if (clones) Object.values(clones).forEach(cloneId => idsToDelete.add(cloneId))
      })
      Object.keys(cloneMap).forEach(srcId => {
        if (Object.values(cloneMap[srcId] || {}).some(cloneId => subtreeIds.has(cloneId))) {
          idsToDelete.add(srcId)
          Object.values(cloneMap[srcId] || {}).forEach(id => idsToDelete.add(id))
        }
      })

      const deleteRecursive = (children: any[], ids: Set<string>): boolean => {
        if (!children || !Array.isArray(children)) return false
        const elementIndex = children.findIndex(child => child?.id != null && ids.has(child.id))
        if (elementIndex !== -1) {
          const deletedElement = children[elementIndex]
          children.splice(elementIndex, 1)
          if (deletedElement.type === ELEMENTS_LOOKUP.COLUMN) {
            const parentRow = children.find((c: any) => c.children?.includes(deletedElement))
            if (parentRow && parentRow.children.length === 0) {
              const rowIndex = children.indexOf(parentRow)
              children.splice(rowIndex, 1)
            }
          }
          return true
        }
        for (let i = 0; i < children.length; i++) {
          const child = children[i]
          if (child?.children && Array.isArray(child.children)) {
            if (deleteRecursive(child.children, ids)) {
              // Only remove empty containers (COLUMN / ROW). Block elements
              // like TEXT / IMAGE / TABLE / VIDEO / PRODUCT_LIST can legitimately
              // exist with zero fillable children, so they must not be
              // cascade-deleted just because their last fillable child was.
              const isContainer = child.type === ELEMENTS_LOOKUP.COLUMN || child.type === ELEMENTS_LOOKUP.ROW
              if (isContainer && child.children.length === 0) {
                children.splice(i, 1)
                i--
              }
              return true
            }
          }
        }
        return false
      }

      let elementDeleted = false
      for (const page of this.pages) {
        const pageSection = page.component?.options?.[sectionType]
        if (!pageSection) continue
        if (!pageSection.children) {
          pageSection.children = []
          continue
        }
        while (deleteRecursive(pageSection.children, idsToDelete)) {
          elementDeleted = true
        }
        if (pageSection.children?.length > 0) {
          pageSection.children = pageSection.children.flatMap((child: any) => {
            if (child.type === ELEMENTS_LOOKUP.COLUMN && (!child.children || child.children.length === 0)) return []
            if (
              child.type === ELEMENTS_LOOKUP.ROW &&
              child.children?.length === 1 &&
              child.children[0]?.type === ELEMENTS_LOOKUP.COLUMN &&
              child.children[0]?.children?.length === 1
            )
              return [child.children[0].children[0]]
            if (child.type === ELEMENTS_LOOKUP.ROW && (!child.children || child.children.length === 0)) return []
            return child
          })
        }
      }

      if (elementDeleted) {
        subtreeIds.forEach(id => {
          if (cloneMap[id]) delete cloneMap[id]
          else {
            const sourceId = Object.keys(cloneMap).find(srcId => cloneMap[srcId] && Object.values(cloneMap[srcId]).includes(id))
            if (sourceId) delete cloneMap[sourceId]
          }
        })
        if (idsToDelete.has(this.activeElementId)) {
          this.updateActiveElement(null)
        }
        this.createRedoStack()
      }
    },
    deleteElementFromPage(elementId: string, pageId: string) {
      if (!this.isDocumentEditable) return
      const pageIndex = this.pages.findIndex(page => pageId === page.id)
      if (pageIndex > -1) {
        const page = this.pages[pageIndex]
        const elementIndex = page.children?.findIndex(element => element.id === elementId)
        if (elementIndex !== undefined && elementIndex > -1) {
          page.children?.splice(elementIndex, 1)
        }
      }
      this.createRedoStack()
    },
    cloneElement(elementId: string) {
      if (!this.isDocumentEditable) return
      if (!elementId) return
      let newElement: any

      // Check if element is in a header or footer section
      const sectionType = this.getElementSection(elementId)
      const isInSection = sectionType === PAGE_SECTIONS.HEADERS || sectionType === PAGE_SECTIONS.FOOTERS

      const createNestedClone = (element: IElement, pageIndex: number) => {
        let clone: IElement = {
          ...cloneDeep(element),
          id: generateElementId(),
          children: [],
        }
        if (element.type === ELEMENTS_LOOKUP.TEXT_FIELD) {
          clone = {
            ...clone,
            component: {
              ...clone.component,
              options: {
                ...clone.component.options,
                fieldId: `text_field_${getNextCount()}`,
              },
            },
          }
        }

        if (element.component?.isDraggable) {
          const position = {
            top: (element?.responsiveStyles?.large?.position?.top || 0) + 10,
            left: (element?.responsiveStyles?.large?.position?.left || 0) + 10,
            bottom: (element?.responsiveStyles?.large?.position?.bottom || 0) - 10,
            right: (element?.responsiveStyles?.large?.position?.right || 0) - 10,
          }

          /**
           * TODO: This is a temporary fix to ensure that the checkbox is not placed in the wrong position
           */
          // if (element.type === ELEMENTS_LOOKUP.CHECKBOX) {
          //   const group = this.getElementGroup(elementId)
          //   if (group) {
          //     const pos = getNewPositionForElement(
          //       elementId,
          //       pageIndex,
          //       PositionTypeEnum.BOTTOM_TOP
          //     )
          //     if (pos) {
          //       position = pos
          //     }
          //   }
          // }

          clone = {
            ...clone,
            pageIndex: pageIndex,
            pageId: this.pages[pageIndex]?.id,
            responsiveStyles: {
              ...clone.responsiveStyles,
              large: {
                ...clone.responsiveStyles.large,
                position: {
                  ...clone.responsiveStyles.large.position,
                  ...position,
                },
              },
            },
          }
        }
        return clone
      }

      const findAndCloneElement = (elements: any[], pageIndex: number): boolean => {
        for (let i = 0; i < elements.length; i++) {
          const element = elements[i]

          if (element.id === elementId) {
            newElement = createNestedClone(element, pageIndex)

            elements.splice(i + 1, 0, newElement)
            this.updateActiveElement(newElement)
            return true
          }

          if (element.children) {
            const found = findAndCloneElement(element.children, pageIndex)
            if (found) return true
          }
        }
        return false
      }

      if (isInSection) {
        // Find the page that contains the element in the section
        const pageIndex = this.pages.findIndex(page => {
          const sectionChildren = page.component?.options?.[sectionType as string]?.children || []
          return this.findElementInChildren(elementId, sectionChildren, page, sectionType as ElementTypes)
        })

        if (pageIndex > -1) {
          const page = this.pages[pageIndex]
          const sectionChildren = page.component?.options?.[sectionType as string]?.children || []

          findAndCloneElement(sectionChildren, pageIndex)

          // Propagate the change to all pages
          this.updateSectionChangeToEveryPage(page.id, sectionType as string)
        }
      } else {
        // Original logic for content area
        const pageIndex = this.pages.findIndex(page =>
          page.children?.some(element => {
            if (element.type === ELEMENTS_LOOKUP.ROW) {
              return element.children?.some(child => {
                if (child.type === ELEMENTS_LOOKUP.COLUMN) {
                  return child.children?.some(c => {
                    if (c.id === elementId) return true
                    if (c.children && c.children.length) {
                      return c.children.some(cc => cc.id === elementId)
                    }
                  })
                }
                return child.id === elementId
              })
            } else {
              if (element.id === elementId) return true
              if (element.children && element.children.length) {
                return element.children.some(child => child.id === elementId)
              }
            }
          })
        )
        if (pageIndex > -1) {
          const page = this.pages[pageIndex]
          findAndCloneElement(page.children, pageIndex)
        }
      }

      this.createRedoStack()
      return newElement
    },
    clearEditorInstance() {
      if (!this.isDocumentEditable) return
      this.editor = undefined
    },
    getCommonPageSection(sectionType: string) {
      if (!this.pages[0]?.component?.options[sectionType]) return
      return this.pages[0]?.component?.options[sectionType]
    },
    toggleShowHeaderSettings() {
      this.showHeaderSettings = !this.showHeaderSettings
      if (!this.showHeaderSettings) this.updateActiveElement(null)
    },
    updateHeaderSelectedFlag(flag: boolean) {
      this.headerSelected = flag
    },
    updateHeaderSelected(flag: boolean) {
      if (!this.isDocumentEditable) return
      this.headerSelected = flag

      let newHeader: IElement | undefined = undefined

      this.pages = this.pages.map(page => {
        const headerAlreadyExists = page?.component?.options?.headers || undefined
        newHeader = flag ? headerAlreadyExists || createElement({ type: ELEMENTS_LOOKUP.PAGE_SECTION }) : undefined

        return {
          ...page,
          component: {
            ...page.component,
            options: {
              ...page.component.options,
              headers: newHeader,
            },
          },
        }
      })

      // Update store header section reference once after mapping
      this.headerSection = newHeader

      this.updateSectionActivationState()
    },
    /** Shared logic for opening header section (double-click or cogwheel). Same side-effects every time. */
    openHeaderSectionForEditing(): boolean {
      if (!this.isDocumentEditable) return false
      if (!this.headerSelected) this.updateHeaderSelected(true)
      const section = this.pages[0]?.component?.options?.[PAGE_SECTIONS.HEADERS] ?? this.headerSection
      if (!section?.id) return false
      this.headerSection = section
      ;(section as IElement).type = ELEMENTS_LOOKUP.PAGE_SECTION
      this.updateActiveElement(section as IElement)
      return true
    },
    /** Shared logic for opening footer section (double-click or cogwheel). Same side-effects every time. */
    openFooterSectionForEditing(): boolean {
      if (!this.isDocumentEditable) return false
      if (!this.footerSelected) this.updateFooterSelected(true)
      const section = this.pages[0]?.component?.options?.[PAGE_SECTIONS.FOOTERS] ?? this.footerSection
      if (!section?.id) return false
      this.footerSection = section
      ;(section as IElement).type = ELEMENTS_LOOKUP.PAGE_SECTION
      this.updateActiveElement(section as IElement)
      return true
    },
    updateFooterSelectedFlag(flag: boolean) {
      this.footerSelected = flag
    },
    updateFooterSelected(flag: boolean) {
      if (!this.isDocumentEditable) return
      this.footerSelected = flag

      let newFooter: IElement | undefined = undefined

      this.pages = this.pages.map(page => {
        const footerAlreadyExists = page?.component?.options?.footers || undefined
        newFooter = flag ? footerAlreadyExists || createElement({ type: ELEMENTS_LOOKUP.PAGE_SECTION }) : undefined

        return {
          ...page,
          component: {
            ...page.component,
            options: {
              ...page.component.options,
              footers: newFooter,
            },
          },
        }
      })

      // Update store footer section reference once after mapping
      this.footerSection = newFooter

      // Update section activation state when footer selection changes
      this.updateSectionActivationState()
    },
    updateEditorInstance(editor: typeof Editor) {
      if (!this.isDocumentEditable) return
      this.editor = editor as typeof Editor
    },
    updateActiveElement(element: IElement | null) {
      if (!this.isDocumentEditable) return
      if (element?.id) {
        this.activeElementId = cloneDeep(element).id
        if (element && ![ELEMENTS_LOOKUP.TEXT, ELEMENTS_LOOKUP.TABLE].includes(element.type)) this.clearEditorInstance()
        // this.createRedoStack()
      }
      if (!element) {
        this.activeElementId = null
      }
      // Update section activation state
      this.updateSectionActivationState()
    },
    updateSectionActivationState() {
      const activeElementId = this.activeElementId

      // Update header section activation
      let headerActive = false
      // Condition 1: Header section itself is the active element (double-clicked or newly enabled)
      // Check if activeElement is a PAGE_SECTION and is a header section from any page
      if (activeElementId) {
        // First check: Is the active element a header section from any page?
        for (const page of this.pages) {
          const headerSection = page?.component?.options?.[PAGE_SECTIONS.HEADERS]
          if (headerSection && headerSection.id === activeElementId) {
            headerActive = true
            break
          }
        }
        // Second check: Is an element within the header section active?
        if (!headerActive) {
          const elementSection = (this as any).getElementSection(activeElementId)
          if (elementSection === PAGE_SECTIONS.HEADERS) {
            headerActive = true
          }
        }
      }
      // Note: When activeElement is null/undefined (user clicked outside),
      // header should NOT be active, even if headerSelected is true

      // Update footer section activation
      let footerActive = false
      // Condition 1: Footer section itself is the active element (double-clicked or newly enabled)
      // Check if activeElement is a PAGE_SECTION and is a footer section from any page
      if (activeElementId) {
        // First check: Is the active element a footer section from any page?
        for (const page of this.pages) {
          const footerSection = page?.component?.options?.[PAGE_SECTIONS.FOOTERS]
          if (footerSection && footerSection.id === activeElementId) {
            footerActive = true
            break
          }
        }
        // Second check: Is an element within the footer section active?
        if (!footerActive) {
          const elementSection = (this as any).getElementSection(activeElementId)
          if (elementSection === PAGE_SECTIONS.FOOTERS) {
            footerActive = true
          }
        }
      }
      // Note: When activeElement is null/undefined (user clicked outside),
      // footer should NOT be active, even if footerSelected is true

      // Use $patch to update state properties atomically
      if (this.isHeaderSectionActive !== headerActive || this.isFooterSectionActive !== footerActive) {
        this.$patch({
          isHeaderSectionActive: headerActive,
          isFooterSectionActive: footerActive,
        })
      }
    },
    splitPage(pageId: string, index: number) {
      const pageIndex = this.pages.findIndex(x => x.id === pageId)
      if (pageIndex > -1) {
        const page = this.pages[pageIndex]
        const newPage = createElement({ type: ELEMENTS_LOOKUP.PAGE })
        if (newPage && page.children?.length) {
          const elements = page.children?.splice(index)
          if (elements?.length) {
            newPage.children = elements
            this.pages.splice(pageIndex + 1, 0, newPage)
          }
        } else if (newPage) {
          this.pages.splice(pageIndex + 1, 0, newPage)
        }

        if (newPage && this.headerSelected) {
          this.updateSectionChangeToEveryPage(newPage?.id, PAGE_SECTIONS.HEADERS)
        }

        if (newPage && this.footerSelected) {
          this.updateSectionChangeToEveryPage(newPage?.id, PAGE_SECTIONS.FOOTERS)
        }
      }
    },
    /**
     * Applies the store's header/footer sections to every page.
     * Use after setPages (e.g. PDF merge) or addPage so new pages stay in sync.
     */
    syncHeaderFooterToAllPages() {
      const cloneMap = (this.headerFooterCloneIdMap = this.headerFooterCloneIdMap ?? {})

      if (this.headerSelected && this.headerSection) {
        this.pages.forEach(page => {
          page.component.options[PAGE_SECTIONS.HEADERS] = cloneSectionWithStableFillableIds(this.headerSection!, page.id, cloneMap)
        })
        this.headerSection = this.pages[0]?.component?.options?.[PAGE_SECTIONS.HEADERS]
      }

      if (this.footerSelected && this.footerSection) {
        this.pages.forEach(page => {
          page.component.options[PAGE_SECTIONS.FOOTERS] = cloneSectionWithStableFillableIds(this.footerSection!, page.id, cloneMap)
        })
        this.footerSection = this.pages[0]?.component?.options?.[PAGE_SECTIONS.FOOTERS]
      }

      this.replicateAllHeaderFooterCheckboxGroupsAcrossPages()
    },

    /** For every page as host, replicate header/footer checkbox groups to every other page (separate group per page). */
    replicateAllHeaderFooterCheckboxGroupsAcrossPages() {
      if (!this.isDocumentEditable) return
      for (const hostPage of this.pages) {
        const hid = hostPage.id
        for (const targetPage of this.pages) {
          if (targetPage.id === hid) continue
          if (this.headerSelected && hostPage.component?.options?.headers) {
            this.syncReplicaCheckboxGroupsForSection(hid, targetPage.id, PAGE_SECTIONS.HEADERS)
          }
          if (this.footerSelected && hostPage.component?.options?.footers) {
            this.syncReplicaCheckboxGroupsForSection(hid, targetPage.id, PAGE_SECTIONS.FOOTERS)
          }
        }
      }
    },

    /**
     * After header/footer HTML is cloned to another page, add a separate checkbox group for that page
     * (same pattern as cloneGroupWithNewIds for body), so each page keeps its own group instance.
     */
    syncReplicaCheckboxGroupsForSection(hostPageId: string, targetPageId: string, sectionType: string) {
      if (!this.isDocumentEditable || hostPageId === targetPageId) return
      const hostPage = this.pages.find(p => p.id === hostPageId)
      const section = hostPage?.component?.options?.[sectionType]
      if (!hostPage || !section) return

      const sectionIds = collectElementIdsInSectionSubtree(section as IElement)
      const cloneMap = this.headerFooterCloneIdMap ?? {}
      const candidates = findCheckboxGroupsWhollyOnHostInSection(this.groups, hostPageId, sectionIds, this.pages)

      for (const hostGroup of candidates) {
        const sourceToClone = buildHeaderFooterElementIdMapForTargetPage(hostGroup.children, hostPageId, targetPageId, cloneMap)
        if (!sourceToClone) continue

        const sortedCloneIds = Object.values(sourceToClone).sort()
        if (hasCheckboxGroupReplicaFootprintOnPage(this.groups, targetPageId, sortedCloneIds, this.pages)) continue

        this.groups = filterOutCheckboxGroupReplicaFootprint(this.groups, targetPageId, sortedCloneIds, this.pages)
        const newGroup = cloneCheckboxGroupForReplicatedSectionPage(hostGroup, targetPageId, sourceToClone, this.groups)
        this.groups = [...this.groups, newGroup]
      }
    },

    updateSectionChangeToEveryPage(hostPageId = '', sectionType: string) {
      if (!hostPageId) return
      const hostPageIndex = this.pages.findIndex(x => x.id === hostPageId)
      if (hostPageIndex > -1) {
        const hostPage = this.pages[hostPageIndex]
        const section = hostPage.component.options[sectionType]
        if (section) {
          this.pages.forEach(page => {
            if (page.id !== hostPageId) {
              const clone = cloneSectionWithStableFillableIds(
                section,
                page.id,
                (this.headerFooterCloneIdMap = this.headerFooterCloneIdMap ?? {})
              )
              syncSectionMediaDimensions(clone, section)
              page.component.options[sectionType] = clone
              this.syncReplicaCheckboxGroupsForSection(hostPageId, page.id, sectionType)
            }
          })
        }
      }
    },

    addElement(
      element: IElement,
      pageId: string,
      rowIndex = -1, // Defaults to -1 for top-level insertion
      colIndex = -1, // Defaults to -1 for adding directly to row, 0+ for column
      rowColIndex = -1 // Index of children inside column, 0+ to specify position
    ) {
      // console.log('add element called!!!')
      if (!this.isDocumentEditable) return

      const pageIndex = this.pages.findIndex(x => x.id === pageId)
      if (pageIndex > -1) {
        const page = this.pages[pageIndex]
        if (colIndex > -1) {
          const row = page.children[rowIndex]
          if (row?.type === ELEMENTS_LOOKUP.ROW) {
            const newColumnElement = createElement({
              type: ELEMENTS_LOOKUP.COLUMN,
            })
            if (rowColIndex > -1) {
              this.pages[pageIndex]?.children[rowIndex]?.children[colIndex]?.children?.splice(rowColIndex, 0, element as IElement)
            } else {
              newColumnElement?.children.push(element)
              this.pages[pageIndex]?.children[rowIndex]?.children?.splice(colIndex, 0, newColumnElement as IElement)
              this.pages[pageIndex]?.children[rowIndex]?.children?.forEach((child: IElement) => {
                child.responsiveStyles.large.width = ''
              })
            }
          } else {
            const columnForExistingElement = createElement({
              type: ELEMENTS_LOOKUP.COLUMN,
            })
            const columnForNewElement = createElement({
              type: ELEMENTS_LOOKUP.COLUMN,
            })
            const newRowElement = createElement({ type: ELEMENTS_LOOKUP.ROW })
            columnForExistingElement?.children.push(row)
            columnForNewElement?.children.push(element)
            newRowElement?.children.push(columnForExistingElement as IElement)
            newRowElement?.children.splice(colIndex, 0, columnForNewElement as IElement)
            page.children.splice(rowIndex, 1, newRowElement as IElement)
          }
        } else {
          page.children.splice(rowIndex, 0, element)
          if (element.type === ELEMENTS_LOOKUP.PAGE_BREAK) {
            this.splitPage(pageId, rowIndex)
          }
        }
        this.updateActiveElement(element)
        this.createRedoStack()
      }
    },
    addElementsFromContentLibrary(
      {
        pages,
        groups,
        customValueLinkage,
      }: {
        pages: IElement[]
        groups: GroupFields<GroupFieldsOptions>[]
        customValueLinkage: Record<string, ICustomFieldsLink>
      },
      pageId: string,
      rowIndex = -1, // Defaults to -1 for top-level insertion
      colIndex = -1 // Defaults to -1 for adding directly to row, 0+ for column
    ) {
      if (pages.length === 0) return
      const firstPage = pages[0]
      if (!this.isDocumentEditable) return
      const pageIndex = this.pages.findIndex(x => x.id === pageId)
      if (pageIndex > -1) {
        const page = this.pages[pageIndex]
        if (colIndex === -1) {
          // iterate through  the children in firstPage and add them to the page and change the id
          const { data: clonedFirstPage, idMap } = cloneWithNewIds(firstPage)
          if (clonedFirstPage) {
            if (clonedFirstPage.children && Array.isArray(clonedFirstPage.children) && clonedFirstPage.children.length > 0) {
              clonedFirstPage.children.reverse().forEach(newElement => {
                page.children.splice(rowIndex, 0, newElement)
              })
              this.syncRecipient()
              if (groups && groups.length > 0) {
                // iterate through the groups and update the id of the element
                const updatedGroups = cloneGroupWithNewIds(groups, idMap)
                this.groups = [...this.groups, ...updatedGroups]
              }

              if (customValueLinkage) {
                const reMappedCVLinkage = remapKeysForCVLinkage(customValueLinkage, idMap)
                if (reMappedCVLinkage) {
                  this.addNewElementsToCustomValueLinkage(reMappedCVLinkage)
                }
              }
              this.createRedoStack(true)
            }
          }
        }
      }
    },
    addElementsFromContentLibraryToPage(
      {
        pages,
        groups,
        customValueLinkage,
      }: {
        pages: IElement[]
        groups: GroupFields<GroupFieldsOptions>[]
        customValueLinkage: Record<string, ICustomFieldsLink>
      },
      index: number
    ) {
      if (pages.length === 0) return
      const firstPage = pages[0]
      if (!this.isDocumentEditable) return
      if (firstPage) {
        const { data: clonedFirstPage, idMap } = cloneWithNewIds(firstPage)
        this.pages.splice(index + 1, 0, clonedFirstPage)
        this.syncRecipient()
        if (groups && groups.length > 0) {
          const updatedGroups = cloneGroupWithNewIds(groups, idMap)
          this.groups = [...this.groups, ...updatedGroups]
        }
        if (customValueLinkage) {
          const reMappedCVLinkage = remapKeysForCVLinkage(customValueLinkage, idMap)
          if (reMappedCVLinkage) {
            this.addNewElementsToCustomValueLinkage(reMappedCVLinkage)
          }
        }
        this.createRedoStack(true)
      }
    },
    addElementToSection(
      element: IElement,
      pageId: string,
      rowIndex = -1, // Defaults to -1 for top-level insertion
      colIndex = -1, // Defaults to -1 for adding directly to row, 0+ for column
      rowColIndex = -1, // Index of children inside column, 0+ to specify position
      sectionType = PAGE_SECTIONS.HEADERS
    ) {
      if (!this.isDocumentEditable) return

      // Ensure the element has a unique ID and is a deep copy to avoid reference issues
      const elementToAdd = cloneDeep(element)
      if (!elementToAdd.id) {
        elementToAdd.id = generateElementId()
      }

      const pageIndex = this.pages.findIndex(x => x.id === pageId)
      if (pageIndex > -1) {
        const page = this.pages[pageIndex]
        if (!page.component.options[sectionType]) {
          page.component.options[sectionType] = {}
          page.component.options[sectionType].children = []
        }
        if (!page.component.options[sectionType].children) {
          page.component.options[sectionType].children = []
        }
        const sectionChildren = page.component.options[sectionType].children

        if (colIndex > -1) {
          let effectiveRowIndex = rowIndex
          let row = sectionChildren[effectiveRowIndex]
          if (!row && sectionChildren.length > 0) {
            effectiveRowIndex = Math.min(Math.max(0, rowIndex), sectionChildren.length - 1)
            row = sectionChildren[effectiveRowIndex]
          }
          if (!row) {
            return
          }
          if (row?.type === ELEMENTS_LOOKUP.ROW) {
            const newColumnElement = createElement({
              type: ELEMENTS_LOOKUP.COLUMN,
            })
            if (rowColIndex > -1) {
              this.pages[pageIndex]?.component.options[sectionType].children[effectiveRowIndex]?.children[colIndex]?.children?.splice(
                rowColIndex,
                0,
                elementToAdd as IElement
              )
            } else {
              newColumnElement?.children.push(elementToAdd)
              this.pages[pageIndex].component.options[sectionType]?.children[effectiveRowIndex]?.children?.splice(
                colIndex,
                0,
                newColumnElement as IElement
              )
              this.pages[pageIndex].component.options[sectionType]?.children[effectiveRowIndex]?.children?.forEach((child: IElement) => {
                child.responsiveStyles.large.width = ''
              })
            }
          } else {
            const columnForExistingElement = createElement({
              type: ELEMENTS_LOOKUP.COLUMN,
            })
            const columnForNewElement = createElement({
              type: ELEMENTS_LOOKUP.COLUMN,
            })
            const newRowElement = createElement({ type: ELEMENTS_LOOKUP.ROW })
            columnForExistingElement?.children.push(row)
            columnForNewElement?.children.push(elementToAdd)
            newRowElement?.children.push(columnForExistingElement as IElement)
            newRowElement?.children.splice(colIndex, 0, columnForNewElement as IElement)
            page.component.options[sectionType].children.splice(effectiveRowIndex, 1, newRowElement as IElement)
          }
        } else {
          page.component.options[sectionType].children.splice(rowIndex, 0, elementToAdd)
        }

        //Replicate the same change to every page
        this.updateSectionChangeToEveryPage(pageId, sectionType)
        this.updateActiveElement(elementToAdd)
        this.createRedoStack()
      }
    },
    moveElementInArray<T>(array: T[], fromIndex: number, toIndex: number): T[] {
      if (fromIndex < 0 || fromIndex >= array.length || toIndex < 0 || toIndex >= array.length || fromIndex === toIndex) {
        return array
      }

      const newArray: T[] = [...array]
      const [movedElement] = newArray.splice(fromIndex, 1)
      if (movedElement === undefined) return array
      newArray.splice(toIndex, 0, movedElement)

      return newArray
    },
    moveElement(element: IElement, pageId: string, newIndex: number, oldIndex: number) {
      if (!this.isDocumentEditable) return
      const pageIndex = this.pages.findIndex(x => x.id === pageId)
      if (pageIndex > -1) {
        const elements = this.pages[pageIndex].children
        const elementsWithoutDraggable = elements.filter(child => !child.component.isDraggable)
        const draggableElements = elements.filter(child => child.component.isDraggable)
        this.pages[pageIndex].children = [
          ...this.moveElementInArray(cloneDeep(elementsWithoutDraggable), oldIndex, newIndex),
          ...draggableElements,
        ]
      }
      this.updateActiveElement(element)
      if (![ELEMENTS_LOOKUP.TEXT, ELEMENTS_LOOKUP.TABLE].includes(element.type)) {
        this.clearEditorInstance()
      }
      this.createRedoStack()
    },
    moveElementInSection(element: IElement, pageId: string, newIndex: number, oldIndex: number, sectionType = PAGE_SECTIONS.HEADERS) {
      if (!this.isDocumentEditable) return
      const pageIndex = this.pages.findIndex(x => x.id === pageId)
      if (pageIndex > -1) {
        if (!this.pages[pageIndex].component.options[sectionType]) return
        const elements = this.pages[pageIndex].component.options[sectionType]?.children
        if (!elements?.length) return
        const elementsWithoutDraggable = elements.filter(child => child && child.component && !child.component.isDraggable)
        const draggableElements = elements.filter(child => child && child.component && child.component.isDraggable)
        this.pages[pageIndex].component.options[sectionType].children = [
          ...this.moveElementInArray(cloneDeep(elementsWithoutDraggable), oldIndex, newIndex),
          ...draggableElements,
        ]

        this.updateSectionChangeToEveryPage(pageId, sectionType)
      }
      this.updateActiveElement(element)
      if (![ELEMENTS_LOOKUP.TEXT, ELEMENTS_LOOKUP.TABLE].includes(element.type)) {
        this.clearEditorInstance()
      }
      this.createRedoStack()
    },
    addPage(index = 0) {
      if (!this.isDocumentEditable) return
      const pageElement = createElement({ type: ELEMENTS_LOOKUP.PAGE })

      if (pageElement) {
        const insertPosition = index <= 0 ? 0 : index + 1
        this.pages.splice(insertPosition, 0, pageElement)
        this.syncHeaderFooterToAllPages()
      }

      this.clearEditorInstance()
      this.createRedoStack()
    },
    updateElementOption(params: IUpdateElementOption) {
      if (!this.isDocumentEditable) return

      const { id, value, key, type } = params
      if (type === ELEMENTS_LOOKUP.PAGE) {
        const pageIndex = this.pages.findIndex(x => x.id === id)
        if (pageIndex > -1) {
          this.pages[pageIndex].component.options[key] = value
        }
        this.createRedoStack()
        return
      }

      const updateNestedElement = (elements: any[]) => {
        for (let index = 0; index < elements.length; index++) {
          const element = elements[index]
          // Only update if the ID matches exactly and the element has component.options
          if (element?.id === id && element?.component?.options) {
            element.component.options[key] = value
            return true
          }
          if (element?.children?.length) {
            if (updateNestedElement(element.children)) return true
          }
        }
        return false
      }

      // Iterate over all pages and their nested children to find and update the element
      for (let i = 0; i < this.pages.length; i++) {
        if (updateNestedElement(this.pages[i].children)) {
          /**
           * Apply debounce in case text block changes were made, this is to make sure a redo stack is not created
           * for every character change but only for changes with some delay
           */
          if (type === ELEMENTS_LOOKUP.TEXT) {
            if (shouldCreateNewEditorGroup) {
              this.createRedoStack()
              shouldCreateNewEditorGroup = false
            }
            debouncedCreateRedoStack.call(this)
          } else {
            this.createRedoStack()
          }
          if (shouldScheduleFloatingLayoutAfterOption(key)) {
            schedulePageFloatingLayoutSync(this, this.pages[i].id)
          }
        }

        if (this.pages[i]?.component?.options?.headers?.children) {
          if (updateNestedElement(this.pages[i].component.options.headers.children)) {
            // Ensure the first page has the updated value, then propagate to all pages
            const firstPage = this.pages[0]
            if (firstPage?.component?.options?.[PAGE_SECTIONS.HEADERS]?.children) {
              // Update on first page if not already updated
              if (i !== 0) {
                const updateFirstPage = (elements: any[]): boolean => {
                  for (const element of elements) {
                    if (element?.id === id) {
                      element.component.options[key] = value
                      return true
                    }
                    if (element?.children?.length) {
                      if (updateFirstPage(element.children)) return true
                    }
                  }
                  return false
                }
                updateFirstPage(firstPage.component.options[PAGE_SECTIONS.HEADERS].children)
              }
            }
            // For text elements in sections, use debounced propagation (clone section to all pages so clone ids preserved)
            const hostPageIdHeaders = this.pages[i].id
            if (type === ELEMENTS_LOOKUP.TEXT) {
              const propagationKey = `${id}-${key}-${PAGE_SECTIONS.HEADERS}`
              const existing = pendingSectionPropagations.get(propagationKey)
              if (existing?.debouncedFn) {
                existing.debouncedFn.cancel()
              }
              const debouncedFn = createDebouncedPropagateSectionOption(hostPageIdHeaders, PAGE_SECTIONS.HEADERS, propagationKey)
              pendingSectionPropagations.set(propagationKey, {
                elementId: id,
                key,
                value,
                sectionType: PAGE_SECTIONS.HEADERS,
                hostPageId: hostPageIdHeaders,
                debouncedFn,
              })
              debouncedFn.call(this)
              if (shouldCreateNewEditorGroup) {
                this.createRedoStack()
                shouldCreateNewEditorGroup = false
              }
              debouncedCreateRedoStack.call(this)
            } else {
              this.updateSectionChangeToEveryPage(hostPageIdHeaders, PAGE_SECTIONS.HEADERS)
              this.createRedoStack()
            }
            if (shouldScheduleFloatingLayoutAfterOption(key)) {
              scheduleAllPagesFloatingLayoutSync(this)
            }
            return
          }
        }

        if (this.pages[i]?.component?.options?.footers?.children) {
          if (updateNestedElement(this.pages[i].component.options.footers.children)) {
            // Ensure the first page has the updated value, then propagate to all pages
            const firstPage = this.pages[0]
            if (firstPage?.component?.options?.[PAGE_SECTIONS.FOOTERS]?.children) {
              // Update on first page if not already updated
              if (i !== 0) {
                const updateFirstPage = (elements: any[]): boolean => {
                  for (const element of elements) {
                    if (element?.id === id) {
                      element.component.options[key] = value
                      return true
                    }
                    if (element?.children?.length) {
                      if (updateFirstPage(element.children)) return true
                    }
                  }
                  return false
                }
                updateFirstPage(firstPage.component.options[PAGE_SECTIONS.FOOTERS].children)
              }
            }
            const hostPageIdFooters = this.pages[i].id
            if (type === ELEMENTS_LOOKUP.TEXT) {
              const propagationKey = `${id}-${key}-${PAGE_SECTIONS.FOOTERS}`
              const existing = pendingSectionPropagations.get(propagationKey)
              if (existing?.debouncedFn) {
                existing.debouncedFn.cancel()
              }
              const debouncedFn = createDebouncedPropagateSectionOption(hostPageIdFooters, PAGE_SECTIONS.FOOTERS, propagationKey)
              pendingSectionPropagations.set(propagationKey, {
                elementId: id,
                key,
                value,
                sectionType: PAGE_SECTIONS.FOOTERS,
                hostPageId: hostPageIdFooters,
                debouncedFn,
              })
              debouncedFn.call(this)
              if (shouldCreateNewEditorGroup) {
                this.createRedoStack()
                shouldCreateNewEditorGroup = false
              }
              debouncedCreateRedoStack.call(this)
            } else {
              this.updateSectionChangeToEveryPage(hostPageIdFooters, PAGE_SECTIONS.FOOTERS)
              this.createRedoStack()
            }
            if (shouldScheduleFloatingLayoutAfterOption(key)) {
              scheduleAllPagesFloatingLayoutSync(this)
            }
            return
          }
        }
      }
    },
    propagateElementOptionToAllPages(elementId: string, key: string, value: any, sectionType: string) {
      if (!elementId) return // Safety check: ensure elementId is provided

      // Helper function to update element option in nested structure
      // This function updates ONLY elements with the matching ID
      const updateNestedElementOption = (elements: any[]): void => {
        for (const element of elements) {
          // Only update if the ID matches exactly
          if (element?.id === elementId && element.component?.options) {
            element.component.options[key] = value
          }
          // Continue searching in nested children
          if (element?.children?.length) {
            updateNestedElementOption(element.children)
          }
        }
      }

      // Update the option on all pages that have this section
      for (const page of this.pages) {
        const section = page.component?.options?.[sectionType]
        if (section?.children) {
          updateNestedElementOption(section.children)
        }
      }
      if (shouldScheduleFloatingLayoutAfterOption(key)) {
        scheduleAllPagesFloatingLayoutSync(this)
      }
    },
    flushPendingSectionPropagations(elementId?: string) {
      if (elementId) {
        const keysToFlush: string[] = []
        pendingSectionPropagations.forEach((value, key) => {
          if (value.elementId === elementId) {
            keysToFlush.push(key)
            if (value.debouncedFn) {
              value.debouncedFn.cancel()
            }
            this.updateSectionChangeToEveryPage(value.hostPageId, value.sectionType)
          }
        })
        keysToFlush.forEach(key => pendingSectionPropagations.delete(key))
        if (keysToFlush.length > 0) {
          this.updateEditors = !this.updateEditors
          scheduleAllPagesFloatingLayoutSync(this)
        }
      } else {
        pendingSectionPropagations.forEach(value => {
          if (value.debouncedFn) {
            value.debouncedFn.cancel()
          }
          this.updateSectionChangeToEveryPage(value.hostPageId, value.sectionType)
        })
        this.updateEditors = !this.updateEditors
        pendingSectionPropagations.clear()
        scheduleAllPagesFloatingLayoutSync(this)
      }
    },
    updateElementStyle(params: IUpdateElementOption) {
      if (!this.isDocumentEditable) return
      const { id, value, key, type } = params
      if (type === ELEMENTS_LOOKUP.PAGE) {
        const pageIndex = this.pages.findIndex(x => x.id === id)
        if (pageIndex > -1) {
          this.pages[pageIndex].responsiveStyles.large[key] = cloneDeep(value)
        }
        this.createRedoStack()
        return
      }
      const updateStyle = (children: IElement[]) => {
        for (let i = 0; i < children.length; i++) {
          if (children[i]?.id === id) {
            children[i].responsiveStyles.large[key] = cloneDeep(value)
            return true
          }
          if (children[i]?.children?.length) {
            const found = updateStyle(children[i].children)
            if (found) return true
          }
        }
        return false
      }

      // Iterate over all pages and their nested children to find and update the element's style
      for (const page of this.pages) {
        if (updateStyle(page?.children)) {
          if (shouldScheduleFloatingLayoutAfterStyle(key, id, this.pages)) {
            schedulePageFloatingLayoutSync(this, page.id)
          }
          this.createRedoStack()
          return
        }

        if (page.component.options.headers && page.component.options.headers.children) {
          if (updateStyle(page.component.options.headers.children)) {
            this.updateSectionChangeToEveryPage(page.id, PAGE_SECTIONS.HEADERS)
            if (shouldScheduleFloatingLayoutAfterStyle(key, id, this.pages)) {
              scheduleAllPagesFloatingLayoutSync(this)
            }
            this.createRedoStack()
            this.validateSectionHeights()
            return
          }
        }

        if (page.component.options.footers && page.component.options.footers.children) {
          if (updateStyle(page.component.options.footers.children)) {
            this.updateSectionChangeToEveryPage(page.id, PAGE_SECTIONS.FOOTERS)
            if (shouldScheduleFloatingLayoutAfterStyle(key, id, this.pages)) {
              scheduleAllPagesFloatingLayoutSync(this)
            }
            this.createRedoStack()
            this.validateSectionHeights()
            return
          }
        }
      }
    },
    updateSectionElementStyle(params: IUpdateSectionElementOption) {
      if (!this.isDocumentEditable) return
      const { id, value, key, section: sectionType } = params

      // First try to find the section in any page
      for (const page of this.pages) {
        const section = page.component.options[sectionType]
        if (section?.id === id) {
          // Found the section, update its style
          section.responsiveStyles.large[key] = cloneDeep(value)

          // If this is a header or footer, propagate the change to all pages
          if (sectionType === PAGE_SECTIONS.HEADERS || sectionType === PAGE_SECTIONS.FOOTERS) {
            this.updateSectionChangeToEveryPage(page.id, sectionType)
            this.validateSectionHeights()
            if (shouldScheduleFloatingLayoutAfterStyle(key, id, this.pages)) {
              scheduleAllPagesFloatingLayoutSync(this)
            }
          }

          this.createRedoStack()
          return
        }
      }

      // If not found at section level, search in nested elements
      const updateStyle = (children: IElement[]) => {
        for (let i = 0; i < children.length; i++) {
          if (children[i]?.id === id) {
            children[i].responsiveStyles.large[key] = cloneDeep(value)
            return true
          }
          if (children[i]?.children?.length) {
            const found = updateStyle(children[i].children)
            if (found) return true
          }
        }
        return false
      }

      // Iterate over all pages and their nested children to find and update the element's style
      for (const page of this.pages) {
        if (page.component.options[sectionType]?.children) {
          if (updateStyle(page.component.options[sectionType].children)) {
            if (sectionType === PAGE_SECTIONS.HEADERS || sectionType === PAGE_SECTIONS.FOOTERS) {
              this.updateSectionChangeToEveryPage(page.id, sectionType)
              this.validateSectionHeights()
              if (shouldScheduleFloatingLayoutAfterStyle(key, id, this.pages)) {
                scheduleAllPagesFloatingLayoutSync(this)
              }
            }
            this.createRedoStack()
            return
          }
        }
      }
    },
    updateSectionElementOption(params: IUpdateSectionElementOption) {
      if (!this.isDocumentEditable) return
      const { id, value, key, section: sectionType } = params

      // First try to find the section in any page
      for (const page of this.pages) {
        const section = page.component.options[sectionType]
        if (section?.id === id) {
          // Found the section, update its option
          section[key] = value

          // Update store values if this is the header or footer section
          if (sectionType === PAGE_SECTIONS.HEADERS) {
            if (this.headerSection?.id === id) this.headerSection[key] = value
          } else if (sectionType === PAGE_SECTIONS.FOOTERS) {
            if (this.footerSection?.id === id) this.footerSection[key] = value
          }

          // If this is a header or footer, propagate the change to all pages
          if (sectionType === PAGE_SECTIONS.HEADERS || sectionType === PAGE_SECTIONS.FOOTERS) {
            const firstPage = this.pages[0]
            if (firstPage) {
              // If we updated the section on a different page, also update it on the first page
              // (which should be the source of truth for header/footer sections)
              if (page.id !== firstPage.id) {
                const firstPageSection = firstPage.component.options[sectionType]
                if (firstPageSection?.id === id) {
                  // Update the first page's section as well
                  firstPageSection[key] = value
                }
              }
              // Use the first page as reference for propagation
              this.updateSectionChangeToEveryPage(firstPage.id, sectionType)
            }
          }

          if (
            (sectionType === PAGE_SECTIONS.HEADERS || sectionType === PAGE_SECTIONS.FOOTERS) &&
            shouldScheduleFloatingLayoutAfterOption(key)
          ) {
            scheduleAllPagesFloatingLayoutSync(this)
          }

          this.createRedoStack()
          return
        }
      }
    },
    updateGroupStyle(params: IUpdateElementOption) {
      if (!this.isDocumentEditable) return
      const { id, value, key, type } = params
      if (type === GROUP_LOOKUP.GROUP_FIELDS) {
        const groupIndex = this.groups.findIndex(g => g.id === id)
        if (groupIndex > -1) this.groups[groupIndex].responsiveStyles.large[key as keyof IResponsiveStylesOptions] = value
        this.createRedoStack()
      }
      return
    },
    assignRecipientToEverySignatureElement(recipient: IRecipient) {
      if (!this.isDocumentEditable) return
      if (!recipient.id) return

      const assignRecipient = (elements: IElement[]) => {
        return elements.map(child => {
          if (child.type === ELEMENTS_LOOKUP.SIGNATURE) {
            return {
              ...child,
              component: {
                ...child.component,
                options: {
                  ...child.component.options,
                  [ELEMENTS_META.RECIPIENT]: recipient.id,
                },
              },
            }
          }
          return child
        })
      }

      this.pages = this.pages.map(page => {
        const updatedPage = {
          ...page,
          children: assignRecipient(page.children ?? []),
        }

        if (page.component.options.headers && updatedPage?.component?.options?.headers?.children) {
          updatedPage.component.options.headers.children = assignRecipient(page.component.options.headers.children ?? [])
        }

        if (page.component.options?.footers && updatedPage?.component?.options?.footers?.children) {
          updatedPage.component.options.footers.children = assignRecipient(page.component.options.footers.children ?? [])
        }

        return updatedPage
      })
      this.createRedoStack()
    },
    autoAssignIfRecipientIdIsNotPresentInRecipientList() {
      if (!this.isDocumentEditable) return

      const appStore = useAppStore()
      const primaryRecipient = appStore.document?.recipients.find(r => r.isPrimary)
      if (!primaryRecipient) return

      const { RECIPIENT, ENTITY_NAME } = ELEMENTS_META

      const assignRecipient = (options: any) => {
        const recipientExist = appStore.document?.recipients.some(r => r.id === options[RECIPIENT] && r.entityName === options[ENTITY_NAME])

        if (!recipientExist || !options[RECIPIENT] || !options[ENTITY_NAME]) {
          return {
            ...options,
            [RECIPIENT]: primaryRecipient.id,
            [ENTITY_NAME]: primaryRecipient.entityName,
          }
        }
        return options
      }

      const autoAssign = (children: IElement[]): IElement[] => {
        return children.map(child => {
          if (assignableFields.has(child?.type)) {
            child.component.options = assignRecipient(child.component.options)
          }
          if (child?.children && child?.children?.length) {
            child.children = autoAssign(child.children)
          }
          return child
        })
      }

      this.pages.forEach(page => {
        page.children = autoAssign(page.children ?? [])
        const headerChildren = page.component?.options?.[PAGE_SECTIONS.HEADERS]?.children
        if (headerChildren) {
          page.component.options[PAGE_SECTIONS.HEADERS].children = autoAssign(headerChildren)
        }
        const footerChildren = page.component?.options?.[PAGE_SECTIONS.FOOTERS]?.children
        if (footerChildren) {
          page.component.options[PAGE_SECTIONS.FOOTERS].children = autoAssign(footerChildren)
        }
      })
      this.groups.forEach(group => {
        group.component.options = assignRecipient(group.component.options)
        group.children = group.children.map(child => {
          if (assignableFields.has(child?.type)) {
            child.component.options = assignRecipient(child.component.options)
          }
          return child
        })
      })

      this.createRedoStack()
    },
    autoAssignSenderIfNotPresentInRecipientList() {
      if (!this.isDocumentEditable) return

      const appStore = useAppStore()
      const currentUser = getRecipientParamsFromContact(appStore.userData, RecipientEntityTypeEnum.USERS, false)

      const { RECIPIENT, ENTITY_NAME } = ELEMENTS_META

      const assignRecipient = (options: any) => {
        if (options.recipient === DOCUMENT_SENDER_ID) {
          return {
            ...options,
            [RECIPIENT]: currentUser.id,
            [ENTITY_NAME]: currentUser.entityName,
          }
        }

        return options
      }

      const autoAssign = (children: IElement[]): IElement[] => {
        return children.map(child => {
          if (assignableFields.has(child?.type)) {
            child.component.options = assignRecipient(child.component.options)
            if (child?.children && child?.children?.length) {
              child.children = autoAssign(child.children)
            }
          } else if (child?.children && child?.children?.length) {
            child.children = autoAssign(child.children)
          }
          return child
        })
      }

      this.pages.forEach(page => {
        page.children = autoAssign(page.children ?? [])
        const headerChildren = page.component?.options?.[PAGE_SECTIONS.HEADERS]?.children
        if (headerChildren) {
          page.component.options[PAGE_SECTIONS.HEADERS].children = autoAssign(headerChildren)
        }
        const footerChildren = page.component?.options?.[PAGE_SECTIONS.FOOTERS]?.children
        if (footerChildren) {
          page.component.options[PAGE_SECTIONS.FOOTERS].children = autoAssign(footerChildren)
        }
      })
      this.groups.forEach(group => {
        group.component.options = assignRecipient(group.component.options)
        group.children = group.children.map(child => {
          if (assignableFields.has(child?.type)) {
            child.component.options = assignRecipient(child.component.options)
            if (child && child?.component) {
              child.component.options = assignRecipient(child.component.options)
            }
          } else if (child?.children && child?.children?.length) {
            child.children = autoAssign(child.children)
          }
          return child
        })
      })
      this.createRedoStack()
    },
    getAllFillableElements() {
      return flatChildrens(this.pages).filter((element): element is IElement => element != null && assignableFields.has(element.type))
    },
    syncContactIdInLinkedCustomValues() {
      const customValueLinkage = this.customValueLinkage
      const fillableElementMap = new Map<string, any>()
      flatChildrens(this.pages).forEach(element => {
        if (element && fillableFields.has(element.type)) {
          fillableElementMap.set(element.id, element)
        }
      })
      Object.keys(customValueLinkage).forEach(elementId => {
        const element = fillableElementMap.get(elementId)
        if (element && element?.component?.options?.recipient) {
          customValueLinkage[elementId].contactId = element.component.options.recipient
        }
      })
    },
    getContactCustomValueLinkMapping() {
      const customValueLinkage = this.customValueLinkage
      const data = Object.keys(customValueLinkage).reduce(
        (acc, elementId) => {
          const customFields = customValueLinkage[elementId].customFields
          const contactId = customValueLinkage[elementId].contactId
          if (contactId) {
            acc[contactId] = acc[contactId] ? [...acc[contactId], ...customFields] : [...customFields]
          }
          return acc
        },
        {} as Record<string, any>
      )
      return data
    },
    setActiveElementId(id: string) {
      if (!this.isDocumentEditable) return
      this.activeElementId = id
      // Update section activation state
      this.updateSectionActivationState()
    },
    syncRecipient() {
      const appStore = useAppStore()
      const { updateDocumentOption } = useElementOption()
      const recipients = appStore.document?.recipients
      const fillableElements = this.getAllFillableElements()
      const fillableElementsNotPartOfAnyGroup = removeFillableElementsWhichIsPartOfGroup(fillableElements, this.groups)

      const allAssignableFieldsWithGroups = [...this.groups, ...fillableElementsNotPartOfAnyGroup]

      const allRecipient = removeUserFromRecipientIfElementIsNotAssigned(allAssignableFieldsWithGroups, recipients)

      const userData = appStore.userData
      const otherRecipients = userData ? getAdditionalRecipientsWithRoles(allRecipient, fillableElements, userData) : []

      let mergedRecipients = [...allRecipient]
      if (otherRecipients.length > 0) {
        mergedRecipients = [...allRecipient, ...otherRecipients]
      }

      const allRecipientsWithRoles = updateRecipientRolesIfElementIsAssigned(allAssignableFieldsWithGroups, mergedRecipients)

      updateDocumentOption(APP_STORE_META.RECIPIENTS, allRecipientsWithRoles)
    },
    syncGroupRecipient() {
      const updateNestedChild = (element: IElement, ids: string[], groupOptions: any) => {
        if (!element.children) return
        element.children.forEach(child => {
          if (ids.includes(child.id)) {
            // For card save element business user is not allowed by payment widget
            // Skip assignment if element is SaveCardDetails and recipient is a user
            if (child.type === ELEMENTS_LOOKUP.SAVE_CARD_DETAILS && groupOptions.entityName === RecipientEntityTypeEnum.USERS) {
              return
            }
            child.component.options.recipient = groupOptions.recipient
            child.component.options.entityName = groupOptions.entityName
          }
          if (child.children) {
            updateNestedChild(child, ids, groupOptions)
          }
        })
      }
      const applyGroupToSectionChildren = (
        children: IElement[] | undefined,
        g: { component: { options: { recipient: string; entityName?: string } }; children: { elementId: string }[] }
      ) => {
        if (!children) return
        const elementIds = g.children.map(({ elementId }) => elementId)
        children.forEach(element => {
          if (elementIds.includes(element.id)) {
            element.component.options.recipient = g.component.options.recipient
            element.component.options.entityName = g.component.options.entityName
          } else {
            updateNestedChild(element, elementIds, g.component.options)
          }
        })
      }
      this.groups.forEach(g => {
        const elementIds = g.children.map(({ elementId }) => elementId)
        if (g.component.options.recipient && g.component.options.entityName) {
          this.pages.forEach(page => {
            page.children?.forEach(element => {
              if (elementIds.includes(element.id)) {
                // For card save element business user is not allowed by payment widget
                // Skip assignment if element is SaveCardDetails and recipient is a user
                if (
                  element.type === ELEMENTS_LOOKUP.SAVE_CARD_DETAILS &&
                  g.component.options.entityName === RecipientEntityTypeEnum.USERS
                ) {
                  return
                }
                element.component.options.recipient = g.component.options.recipient
                element.component.options.entityName = g.component.options.entityName
              } else {
                updateNestedChild(element, elementIds, g.component.options)
              }
            })
            applyGroupToSectionChildren(page.component?.options?.[PAGE_SECTIONS.HEADERS]?.children, g)
            applyGroupToSectionChildren(page.component?.options?.[PAGE_SECTIONS.FOOTERS]?.children, g)
          })
        }
      })
    },
    syncCurrency(currencyCode: string) {
      if (!this.isDocumentEditable) return
      this.pages = this.pages.map(page => {
        return Object.assign(page, {
          children: page.children?.map(child => {
            if (child.type === ELEMENTS_LOOKUP.PRODUCT_LIST) {
              return {
                ...child,
                component: {
                  ...child.component,
                  options: {
                    ...child.component.options,
                    [ELEMENTS_META.CURRENCY_CODE]: currencyCode,
                    [ELEMENTS_META.LINE_ITEMS]: child.component.options.lineItems.map((lineItem: any) => ({
                      ...lineItem,
                      currency: currencyCode,
                    })),
                  },
                },
              }
            }
            if (child.type === ELEMENTS_LOOKUP.ROW) {
              return {
                ...child,
                children: child.children?.map(column => {
                  return {
                    ...column,
                    children: column.children?.map(el => {
                      if (el.type === ELEMENTS_LOOKUP.PRODUCT_LIST) {
                        return {
                          ...el,
                          component: {
                            ...el.component,
                            options: {
                              ...el.component.options,
                              [ELEMENTS_META.CURRENCY_CODE]: currencyCode,
                              [ELEMENTS_META.LINE_ITEMS]: el.component.options.lineItems.map((lineItem: any) => ({
                                ...lineItem,
                                currency: currencyCode,
                              })),
                            },
                          },
                        }
                      } else {
                        return el
                      }
                    }),
                  }
                }),
              }
            }
            return child
          }),
        })
      })
    },
    syncGroup() {
      const elementIds = flatChildrens(this.pages).map(({ id }) => id)

      // filtering & updating the group elements, if the element is available in the pages.
      this.groups.forEach(g => {
        g.children = g.children.filter(({ elementId }) => elementIds.includes(elementId))
      })

      // deleting the group if its having child length of 1 or less.
      this.groups = this.groups.filter(g => g.children?.length > 1)
    },
    getFillableFields() {
      const fillableFields = new Set([
        ELEMENTS_LOOKUP.TEXT_FIELD,
        ELEMENTS_LOOKUP.DATE_FIELD,
        ELEMENTS_LOOKUP.CHECKBOX,
        ELEMENTS_LOOKUP.SAVE_CARD_DETAILS,
      ])
      return flatChildrens(this.pages)
        .filter(child => fillableFields.has(child.type))
        .map(child => {
          const {
            component: { options: option },
          } = child
          return {
            value: option.text || ('' as string),
            fieldId: option.fieldId as string,
            isRequired: option.required as boolean,
            recipient: option.recipient,
            hasCompleted: option.text ? true : false,
            entityType: option.entityName as RecipientEntityTypeEnum,
            id: child.id,
            type: child.type,
          }
        }) as UpdateSignatureParams['fillableFields']
    },
    getAllProductList() {
      return flatChildrens(this.pages).filter(element => element.type === ELEMENTS_LOOKUP.PRODUCT_LIST)
    },
    setFlatCustomValues(values: any) {
      this.flatCustomValues = values
    },
    isRecurringProduct() {
      return flatChildrens(this.pages)
        .filter(child => child.type === ELEMENTS_LOOKUP.PRODUCT_LIST) // Filter for relevant children
        .map(child =>
          (child.component?.options as ProductListElementOptions)?.lineItems?.find(
            (item: LineItem) => item.priceType === PriceTypeEnum.RECURRING && !item?.isSetupFeeItem
          )
        )
        .find((lineItem): lineItem is LineItem => !!lineItem)
    },
    hasNoLineItems() {
      return flatChildrens(this.pages)
        .filter(child => child.type === ELEMENTS_LOOKUP.PRODUCT_LIST) // Filter for relevant children
        .map(child => (child.component?.options as ProductListElementOptions)?.lineItems?.length)
        .every(length => length === 0)
    },
    syncCustomFieldsForLinkage() {
      const keys = Object.keys(this.customValueLinkage)
      // if the key is not exist in the page fillable fields then remove the key from the custom value linkage
      const fillableElements = flatChildrens(this.pages).filter(element => fillableFields.has(element.type))

      // iterate over the custom value linkage and check
      // if the key is not exist in the fillable elements then remove the key from the custom value linkage
      keys.forEach(key => {
        const isKeyExist = fillableElements.some(element => element.id === key)
        if (!isKeyExist) {
          delete this.customValueLinkage[key]
        }
      })

      // if the primary recipient is there then only do the below operation.
      // otherwise, cloneFromTheTemplate will have an issue.
      const appStore = useAppStore()
      const primaryContact = appStore?.primaryContact
      if (primaryContact && primaryContact.id) {
        fillableElements.forEach(element => {
          if (this.customValueLinkage[element.id]) {
            const contactId = this.customValueLinkage[element.id].contactId
            if (!contactId) {
              delete this.customValueLinkage[element.id]
            }
            if (contactId && element.component?.options?.recipient) {
              if (element.component?.options?.entityName !== RecipientEntityTypeEnum.CONTACTS) {
                delete this.customValueLinkage[element.id]
              }

              if (element.component?.options?.recipient && contactId && element.component?.options?.recipient !== contactId) {
                this.customValueLinkage[element.id].contactId = element.component?.options?.recipient
              }
            }
          }
        })
      }
    },
    deleteElementFromNestedChildren(id: string) {
      if (!this.isDocumentEditable) return
      const findAndDelete = children => {
        for (let i = 0; i < children.length; i++) {
          if (children[i]?.id === id) {
            children?.splice(i, 1)
            return true
          }
          if (children[i]?.children && children[i]?.children?.length) {
            const found = findAndDelete(children[i].children)
            if (found) return true
          }
        }
        return false
      }

      this.pages.forEach(page => {
        findAndDelete(page.children)
      })
      this.createRedoStack()
    },
    addElementToElementChildren(parentId: string, newElement: IElement) {
      if (!this.isDocumentEditable) return

      const findAndAdd = children => {
        for (let i = 0; i < children.length; i++) {
          if (children[i].id === parentId) {
            children[i].children = children[i].children || []
            children[i].children.push(newElement)
            return true
          }
          if (children[i].children && children[i].children.length) {
            const found = findAndAdd(children[i].children)
            if (found) return true
          }
        }
        return false
      }

      // Check if parentId belongs to a Page object
      for (const page of this.pages) {
        if (page.id === parentId) {
          page.children = page.children || []
          page.children.push(newElement)
          return
        }

        // Otherwise, search within the children of the Page
        if (findAndAdd(page.children)) {
          return
        }
      }
      this.createRedoStack()
    },
    moveElementById(elementId: string, newParentId: string, sectionType?: string | null) {
      let elementToMove: IElement | null = null

      const findAndRemove = (children: IElement[]): IElement[] => {
        return children
          .map((child: IElement) => {
            if (child.id === elementId) {
              elementToMove = child
              return null
            }

            if (child.children && child.children.length > 0) {
              child.children = findAndRemove(child.children).filter(Boolean)
            }
            return child
          })
          .filter(Boolean) as IElement[]
      }

      this.pages.forEach(page => {
        // Check in content section
        page.children = findAndRemove(page.children)

        // Check in header section
        if (page.component?.options[PAGE_SECTIONS.HEADERS]?.children) {
          page.component.options[PAGE_SECTIONS.HEADERS].children = findAndRemove(page.component.options[PAGE_SECTIONS.HEADERS].children)
        }

        // Check in footer section
        if (page.component?.options[PAGE_SECTIONS.FOOTERS]?.children) {
          page.component.options[PAGE_SECTIONS.FOOTERS].children = findAndRemove(page.component.options[PAGE_SECTIONS.FOOTERS].children)
        }
      })

      if (elementToMove) {
        const findAndAdd = (children: IElement[]): boolean => {
          for (let i = 0; i < children.length; i++) {
            if (children[i]?.id === newParentId) {
              children[i].children = children[i].children || []
              children[i].children.push(elementToMove as IElement)
              return true
            }

            if (children[i]?.children && children[i].children.length > 0) {
              const found = findAndAdd(children[i].children)
              if (found) return true
            }
          }
          return false
        }

        let elementAdded = false
        let addedToSectionPageId: string | null = null
        let addedToSectionType: string | null = null

        for (const page of this.pages) {
          // If sectionType is provided and newParentId matches page ID, add directly to that section
          if (sectionType && page?.id === newParentId) {
            if (sectionType === PAGE_SECTIONS.HEADERS) {
              if (!page.component.options[PAGE_SECTIONS.HEADERS]) {
                page.component.options[PAGE_SECTIONS.HEADERS] = createElement({
                  type: ELEMENTS_LOOKUP.PAGE_SECTION,
                })
              }
              if (!page.component.options[PAGE_SECTIONS.HEADERS].children) {
                page.component.options[PAGE_SECTIONS.HEADERS].children = []
              }
              page.component.options[PAGE_SECTIONS.HEADERS].children.push(elementToMove)
              elementAdded = true
              addedToSectionPageId = page.id
              addedToSectionType = sectionType
              break
            } else if (sectionType === PAGE_SECTIONS.FOOTERS) {
              if (!page.component.options[PAGE_SECTIONS.FOOTERS]) {
                page.component.options[PAGE_SECTIONS.FOOTERS] = createElement({
                  type: ELEMENTS_LOOKUP.PAGE_SECTION,
                })
              }
              if (!page.component.options[PAGE_SECTIONS.FOOTERS].children) {
                page.component.options[PAGE_SECTIONS.FOOTERS].children = []
              }
              page.component.options[PAGE_SECTIONS.FOOTERS].children.push(elementToMove)
              elementAdded = true
              addedToSectionPageId = page.id
              addedToSectionType = sectionType
              break
            }
          }

          // Try to add to content section
          if (page?.id === newParentId && !sectionType) {
            page.children = page.children || []
            page.children.push(elementToMove)
            elementAdded = true
            break
          }

          // Try to add to content section's children
          if (findAndAdd(page.children || [])) {
            elementAdded = true
            break
          }

          // Try to add to header section's children
          if (page.component?.options[PAGE_SECTIONS.HEADERS]?.children) {
            if (findAndAdd(page.component.options[PAGE_SECTIONS.HEADERS].children)) {
              elementAdded = true
              addedToSectionPageId = page.id
              addedToSectionType = PAGE_SECTIONS.HEADERS
              break
            }
          }

          // Try to add to footer section's children
          if (page.component?.options[PAGE_SECTIONS.FOOTERS]?.children) {
            if (findAndAdd(page.component.options[PAGE_SECTIONS.FOOTERS].children)) {
              elementAdded = true
              addedToSectionPageId = page.id
              addedToSectionType = PAGE_SECTIONS.FOOTERS
              break
            }
          }
        }

        if (addedToSectionPageId && addedToSectionType) {
          this.updateSectionChangeToEveryPage(addedToSectionPageId, addedToSectionType)
        }

        // If element wasn't added anywhere, add it back to its original location as fallback
        if (!elementAdded && elementToMove) {
          // Try to find where it was originally and add it back there
          // This is a safety net to prevent element loss
          for (const page of this.pages) {
            // Try header section first
            if (page.component?.options[PAGE_SECTIONS.HEADERS]?.children) {
              page.component.options[PAGE_SECTIONS.HEADERS].children.push(elementToMove)
              elementAdded = true
              break
            }
            // Try footer section
            if (!elementAdded && page.component?.options[PAGE_SECTIONS.FOOTERS]?.children) {
              page.component.options[PAGE_SECTIONS.FOOTERS].children.push(elementToMove)
              elementAdded = true
              break
            }
            // Try content section as last resort
            if (!elementAdded) {
              page.children = page.children || []
              page.children.push(elementToMove)
              elementAdded = true
              break
            }
          }
        }

        this.createRedoStack()
      } else {
        this.createRedoStack()
      }
    },
    moveFloatingElement(
      elementId: string,
      options: {
        elementId: string
        newParentId: string
        sectionType?: string | null
        styles: {
          top: number
          left: number
          bottom: number
          right: number
          height: number
          width: number
          preferBottom: boolean
          preferRight: boolean
        }
      }
    ) {
      // First, check if the element is in a section
      const currentSectionType = this.getElementSection(elementId)
      const currentPageId = currentSectionType
        ? this.getPageIdBySectionElementId(elementId, currentSectionType)
        : this.getPageIdByElementId(elementId)

      // If element is being moved to a different parent or section, use moveElementById
      // Otherwise, just update the position styles (element stays in same location)
      const isMovingToDifferentParent = options.newParentId !== currentPageId || options.sectionType !== currentSectionType

      if (isMovingToDifferentParent) {
        this.moveElementById(elementId, options.newParentId, options.sectionType)
      }

      // Always update position styles (even if not moving parent)
      const element = flatChildrens(this.pages).find(el => el.id === elementId)
      if (!element) {
        // Element not found - this shouldn't happen, but if it does, return early
        return
      }

      // Use updateSectionElementStyle if element is in a section, otherwise use updateElementStyle
      if (currentSectionType) {
        this.updateSectionElementStyle({
          id: elementId,
          key: 'position',
          value: {
            top: options.styles.top,
            left: options.styles.left,
            bottom: options.styles.bottom,
            right: options.styles.right,
            preferBottom: options.styles.preferBottom,
            preferRight: options.styles.preferRight,
          },
          section: currentSectionType,
        })

        // Update dimensions if provided
        if (options.styles.height && options.styles.width) {
          this.updateSectionElementStyle({
            id: elementId,
            key: 'dimensions',
            value: {
              height: options.styles.height,
              width: options.styles.width,
            },
            section: currentSectionType,
          })
        }
      } else {
        this.updateElementStyle({
          id: elementId,
          key: 'position',
          value: {
            top: options.styles.top,
            left: options.styles.left,
            bottom: options.styles.bottom,
            right: options.styles.right,
            preferBottom: options.styles.preferBottom,
            preferRight: options.styles.preferRight,
          },
          type: element.type,
        })

        // Update dimensions if provided
        if (options.styles.height && options.styles.width) {
          this.updateElementStyle({
            id: elementId,
            key: 'dimensions',
            value: {
              height: options.styles.height,
              width: options.styles.width,
            },
            type: element.type,
          })
        }
      }

      // Clear isGhost flag after drag ends
      this.updateElementOption({
        id: elementId,
        key: 'isGhost',
        value: false,
      })
    },
    setAllLineItemsAndQtyOptional(value: boolean) {
      if (!this.isDocumentEditable) return
      this.pages = this.pages.map(page => {
        return Object.assign(page, {
          children: page.children?.map(child => {
            if (child.type === ELEMENTS_LOOKUP.PRODUCT_LIST) {
              return {
                ...child,
                component: {
                  ...child.component,
                  options: {
                    ...child.component.options,
                    [ELEMENTS_META.LINE_ITEMS]: child.component.options?.lineItems.map((lineItem: LineItem) => ({
                      ...lineItem,
                      optional: value,
                      qtyEditable: value,
                      // if optional value is false, then we need  disabled: true, selected: true
                      ...(value
                        ? { disabled: false }
                        : {
                            disabled: true,
                            selected: true,
                          }),
                    })),
                  },
                },
              }
            } else if (child.type === ELEMENTS_LOOKUP.ROW) {
              return {
                ...child,
                children: child.children?.map(column => {
                  return {
                    ...column,
                    children: column.children?.map(el => {
                      if (el.type === ELEMENTS_LOOKUP.PRODUCT_LIST) {
                        return {
                          ...el,
                          component: {
                            ...el.component,
                            options: {
                              ...el.component.options,
                              [ELEMENTS_META.LINE_ITEMS]: el.component.options?.lineItems.map((lineItem: LineItem) => ({
                                ...lineItem,
                                optional: value,
                                qtyEditable: value,
                                ...(value
                                  ? { disabled: false }
                                  : {
                                      disabled: true,
                                      selected: true,
                                    }),
                              })),
                            },
                          },
                        }
                      } else {
                        return el
                      }
                    }),
                  }
                }),
              }
            }
            return child
          }),
        })
      })
    },
    setLoadedFonts(fonts: string[]) {
      this.loadedFonts = fonts
    },
    setLoadedFontsFromEditor(fonts: string[]) {
      const uniqueFonts = [...new Set([...fonts, ...(this.fontsToLoad ? this.fontsToLoad : [])])]
      this.fontsToLoad = uniqueFonts
    },
    getCustomValueLinkageErrors() {
      this.syncCustomFieldsForLinkage()
      const appStore = useAppStore()
      const contactCustomValueMapping = this.getContactCustomValueLinkMapping()
      const recipients = appStore?.document?.recipients || []
      const customValueLinkageErrors = checkForDuplicatesFieldKeyInCustomValueLinkage(contactCustomValueMapping, recipients)
      return customValueLinkageErrors || []
    },
    getImageSizeErrors(): string[] {
      const errors: string[] = []
      const elements = flatChildrens(this.pages)
      elements.forEach((child: IElement) => {
        if (!child || child.type !== ELEMENTS_LOOKUP.IMAGE) return

        const src = (child as any)?.component?.options?.src
        if (!src) return

        const height = child?.responsiveStyles?.large?.height
        const width = child?.responsiveStyles?.large?.width

        const heightNum = height !== '' && height !== undefined && height !== null ? Number(height) : NaN
        const widthNum = width !== '' && width !== undefined && width !== null ? Number(width) : NaN

        const invalid = !height || !width || Number.isNaN(heightNum) || Number.isNaN(widthNum) || heightNum <= 0 || widthNum <= 0

        if (!invalid) return

        errors.push((child as any)?.id)
      })
      return errors
    },
    recomputeFloatingLayoutPositionsForPage(pageId: string) {
      if (typeof document === 'undefined') return
      const pageIndex = this.pages.findIndex(p => p.id === pageId)
      if (pageIndex < 0) return
      const page = this.pages[pageIndex]
      const ids = collectBottomPreferDraggableIdsFromPage(page)
      if (!ids.length) return
      nextTick(() => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            for (const fid of ids) {
              const el = findElementOnSinglePage(page, fid)
              if (!el?.component?.isDraggable) continue
              const pos = el.responsiveStyles?.large?.position
              if (!pos?.preferBottom) continue
              const sectionForMeasure = getFloatingBoundSectionForElementOnPage(page, fid)
              const geometry = computeBottomPreferGeometryFromStoredLayout(el, pageIndex, sectionForMeasure)
              if (!geometry) continue
              if (!bottomPreferLayoutNeedsWrite(pos, geometry)) continue
              applyBottomPreferPositionSync(el, geometry, !!pos.preferRight)
            }
          })
        })
      })
    },
    recomputeFloatingLayoutPositionsForAllPages() {
      if (typeof document === 'undefined') return
      nextTick(() => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            for (let pageIndex = 0; pageIndex < this.pages.length; pageIndex++) {
              const page = this.pages[pageIndex]
              const ids = collectBottomPreferDraggableIdsFromPage(page)
              for (const fid of ids) {
                const el = findElementOnSinglePage(page, fid)
                if (!el?.component?.isDraggable) continue
                const pos = el.responsiveStyles?.large?.position
                if (!pos?.preferBottom) continue
                const sectionForMeasure = getFloatingBoundSectionForElementOnPage(page, fid)
                const geometry = computeBottomPreferGeometryFromStoredLayout(el, pageIndex, sectionForMeasure)
                if (!geometry) continue
                if (!bottomPreferLayoutNeedsWrite(pos, geometry)) continue
                applyBottomPreferPositionSync(el, geometry, !!pos.preferRight)
              }
            }
          })
        })
      })
    },
    getElementSection(elementId: string): string | null {
      // Check in headers
      for (const page of this.pages) {
        const headerSection = page.component?.options?.[PAGE_SECTIONS.HEADERS]
        if (headerSection?.children) {
          const found = this.findElementInChildren(elementId, headerSection.children, headerSection, PAGE_SECTIONS.HEADERS)
          if (found) {
            return PAGE_SECTIONS.HEADERS
          }
        }
      }

      // Check in content (main page children)
      for (const page of this.pages) {
        if (page.children) {
          const found = this.findElementInChildren(elementId, page.children, page, PAGE_SECTIONS.CONTENT)
          if (found) {
            return PAGE_SECTIONS.CONTENT
          }
        }
      }

      // Check in footers
      for (const page of this.pages) {
        const footerSection = page.component?.options?.[PAGE_SECTIONS.FOOTERS]
        if (footerSection?.children) {
          const found = this.findElementInChildren(elementId, footerSection.children, footerSection, PAGE_SECTIONS.FOOTERS)
          if (found) {
            return PAGE_SECTIONS.FOOTERS
          }
        }
      }

      return null
    },
    updatePageNumberOptions(options: { enablePageNumbering?: boolean; pageNumberLocation?: string; pageNumberFormat?: string }) {
      if (!this.isDocumentEditable) return

      // Update store state
      if (options.enablePageNumbering !== undefined) {
        this.enablePageNumbering = options.enablePageNumbering
      }
      if (options.pageNumberLocation !== undefined) {
        this.pageNumberLocation = options.pageNumberLocation
      }
      if (options.pageNumberFormat !== undefined) {
        this.pageNumberFormat = options.pageNumberFormat
      }

      // Update all pages
      this.pages.forEach(page => {
        if (options.enablePageNumbering !== undefined) {
          this.updateElementOption({
            id: page.id,
            key: 'enablePageNumbering',
            value: options.enablePageNumbering,
            type: ELEMENTS_LOOKUP.PAGE,
          })
        }
        if (options.pageNumberLocation !== undefined) {
          this.updateElementOption({
            id: page.id,
            key: 'pageNumberLocation',
            value: options.pageNumberLocation,
            type: ELEMENTS_LOOKUP.PAGE,
          })
        }
        if (options.pageNumberFormat !== undefined) {
          this.updateElementOption({
            id: page.id,
            key: 'pageNumberFormat',
            value: options.pageNumberFormat,
            type: ELEMENTS_LOOKUP.PAGE,
          })
        }
      })

      this.createRedoStack()
    },
    /**
     * Persist measured header/footer heights for the given page (used for PDF page-break calculation).
     * Header/footer replace page margin; these values are saved so we don't rely only on DOM measurement.
     */
    updatePageSectionHeights(pageId: string, heights: { headerHeight?: number; footerHeight?: number }) {
      const pageIndex = this.pages.findIndex(p => p.id === pageId)
      if (pageIndex === -1) return
      const page = this.pages[pageIndex]
      if (heights.headerHeight !== undefined) {
        page.component.options.headerHeight = heights.headerHeight
      }
      if (heights.footerHeight !== undefined) {
        page.component.options.footerHeight = heights.footerHeight
      }
      this.validateSectionHeights()
    },
    /**
     * Validate that header + footer combined height does not exceed page content area (960px).
     * Only runs when header or footer is enabled. Sets sectionHeightValidationError for UI/toast.
     */
    validateSectionHeights() {
      if (!this.headerSelected && !this.footerSelected) {
        this.sectionHeightValidationError = null
        return
      }
      const firstPage = this.pages[0]
      if (!firstPage?.component?.options) {
        this.sectionHeightValidationError = null
        return
      }
      const headerHeight = Number((this.headerSelected && firstPage.component.options.headerHeight) ?? 0) || 0
      const footerHeight = Number((this.footerSelected && firstPage.component.options.footerHeight) ?? 0) || 0
      const combined = headerHeight + footerHeight
      if (combined > MAX_HEADER_FOOTER_COMBINED_HEIGHT) {
        this.sectionHeightValidationError = 'validations.invalid.headerFooterCombinedSizeExceeded'
      } else {
        this.sectionHeightValidationError = null
      }
    },
  },
})
